// Vanilla three.js cornhole scene. Drives the canvas inside VirtualPlay.
//
// Physics is bespoke and very simple: each bag is a free body that
// integrates gravity until it lands. Collision is approximated against:
//   - the board plane (raked at ~12°, height 30cm at back, 8cm at front)
//   - the hole (circular cutout) — bag drops through, falls to grade
//   - the ground plane
// "On board" = z within the board polygon at rest height. "In hole" = fell
// through the hole circle. Returns each throw's result; the page wires
// that to a +N tap on the existing pro-mode frame.

import {
  AmbientLight,
  BoxGeometry,
  CanvasTexture,
  Color,
  DirectionalLight,
  DoubleSide,
  EquirectangularReflectionMapping,
  ExtrudeGeometry,
  Fog,
  Group,
  HemisphereLight,
  LinearFilter,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PCFSoftShadowMap,
  Path,
  PerspectiveCamera,
  PlaneGeometry,
  Quaternion,
  RepeatWrapping,
  RingGeometry,
  SRGBColorSpace,
  Scene,
  Shape,
  Sprite,
  SpriteMaterial,
  Texture,
  TextureLoader,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";

import {
  BUILT_IN_REGISTRY,
  type AssetRegistry,
  type BagAsset,
  type BoardAsset,
  type GameLoadout,
  type SceneAsset,
  lookupBag,
  lookupBoard,
  lookupScene,
} from "./virtual3d-assets";
import {
  playBagInHole,
  playBagLand,
  playBagOnGround,
  startBagSlide,
  stopBagSlide,
} from "./feedback";
import { getTuning, subscribeTuning } from "./virtual3d-tuning";
// Imported through Vite so the URL is content-hashed
// (/assets/grass-yard-<hash>.jpg). Any change to the file bumps the
// hash → guaranteed cache-bust on every device with no service-worker
// dance. Files under public/ are served as-is and cache forever, which
// is what previously trapped users on the old "dead grass" texture
// even after uninstall/reinstall.
import grassYardUrl from "@/assets/grass-yard.jpg";

// Re-exports so existing call sites can keep importing SceneId / scene
// IDs from this module. (Other callers should import from
// ./virtual3d-assets directly.)
export type SceneId = string;

// Internal palette type the canvas painters consume. Built from a
// SceneAsset so we don't pass the whole asset deeper than necessary.
type ScenePalette = SceneAsset["palette"];

// Compressed throw-line distance — feels right on a phone where the
// regulation 27 ft compresses the board to a tiny ribbon on the horizon.
const THROW_LINE_DISTANCE = 7.5;

// Derived board dimensions. Stored on a single object so functions
// downstream don't have to drag 6 constants through their signatures.
// `rake` is computed from rise/run; the rest mirror the BoardAsset.
export type BoardDims = {
  width: number;
  length: number;
  thickness: number;
  holeRadius: number;
  holeFromBack: number;
  rakeRise: number;
  frontLift: number;
  rake: number;
};

export function dimsFromAsset(b: BoardAsset): BoardDims {
  return {
    width: b.width,
    length: b.length,
    thickness: b.thickness,
    holeRadius: b.holeRadius,
    holeFromBack: b.holeFromBack,
    rakeRise: b.rakeRise,
    frontLift: b.frontLift,
    rake: Math.atan2(b.rakeRise, b.length),
  };
}

export type ThrowResult = {
  outcome: "in_hole" | "on_board" | "miss";
  resting: { x: number; y: number; z: number };
};

export type VirtualGameAPI = {
  // Swap the asset registry the renderer reads from. Called by the
  // host (VirtualPlay) whenever React Query refetches the catalog —
  // without this the renderer would keep resolving IDs against the
  // registry captured at MOUNT time, so admin edits to an
  // already-picked asset (e.g. saving new bag alignment in
  // /admin/design) would never reach the live game until full reload.
  setRegistry: (next: AssetRegistry) => void;
  // Swap any subset of assets without destroying the scene.
  setLoadout: (next: GameLoadout) => void;
  // Convenience used by the existing scene chip row.
  setScene: (sceneId: string) => void;
  throwBag: (input: {
    team: 1 | 2;
    powerMs: number; // ms held = power proxy
    dirX: number; // -1..1 lateral aim
    dirY: number; // 0..1 vertical aim (loft)
    // Optional world-space spawn point (used by finger-throw mode so
    // the bag flies from the user's release position). Defaults to a
    // chest-height fixed point when not provided.
    start?: { x: number; y: number; z: number };
    // Player-scoped distance multiplier from the per-device power
    // slider (default 1). Applied on top of the flick-derived
    // distance ramp, NOT capped at the flick ceiling — so >1 genuinely
    // extends reach past the board (overshoot) and <1 falls short.
    // This is the one player-scoped tunable; all other physics is the
    // global admin tuning.
    powerScale?: number;
  }) => Promise<ThrowResult>;
  // Scripted throw for the ghost AI: bag flies on a fixed parabola
  // to a predetermined landing zone for the requested outcome and
  // the result is FORCED — no physics drift can change it. Ghost-N
  // calls this N times per round with on_board (or in_hole + miss)
  // outcomes that sum to exactly N points.
  throwBagScripted: (
    team: 1 | 2,
    outcome: "in_hole" | "on_board" | "miss",
    opts?: {
      // Seconds to hold the bag visible at the launch position before
      // the arc begins. Used by opponent throws to give the player a
      // beat to register "they're about to throw" — without the hold
      // the bag would appear mid-air. Default 0 = no hold.
      holdSeconds?: number;
      // Explicit world-space resting target. When provided, the bag
      // flies to THIS point instead of a fresh random pick inside the
      // outcome zone. Multiplayer/ghost: the originator picks the
      // target up-front, posts it via /throws, and every device
      // (including originator) animates to the same world position.
      target?: { x: number; y: number; z: number };
    },
  ) => Promise<ThrowResult>;
  // Pick a world-space resting position for the given outcome WITHOUT
  // animating. Used by ghost dispatch to commit a target up-front
  // (so the POST can record it) before kicking off the local
  // animation — keeps every device's animation landing at the same
  // point as the originator.
  pickScriptedTarget: (
    team: 1 | 2,
    outcome: "in_hole" | "on_board" | "miss",
  ) => { x: number; y: number; z: number };
  // Update the visible "remaining bags" piles next to the near board.
  setBagsRemaining: (team1: number, team2: number) => void;
  // Track whose turn it is. Kept as a no-op stub now that the rig
  // system is gone — callers in VirtualPlay.tsx still invoke it on
  // every team-switch, and we leave the hook in place so any future
  // turn-driven visuals (highlight, swap bag color in hand, etc.)
  // have somewhere to land.
  setActiveTeam: (team: 1 | 2) => void;
  // Pop one bag off the visible pile and place it in the player's hand.
  // Returns once the (now empty) grab animation settles. Called when
  // the user taps a bag in their pile.
  grabBag: (team: 1 | 2) => Promise<void>;
  resetBoard: () => void;
  destroy: () => void;

  // ----- Finger-throw mode -----
  // The player's bag pops as a 3D mesh anchored to the bottom of the
  // screen so the user can touch + flick it directly. Always on now
  // that the rig system has been removed.
  //
  // Convert a screen-space pointer position to a hit test against the
  // bag — returns true if the touch started ON the bag (so the FE
  // can start "magnetic" dragging).
  isFingerBagHit: (clientX: number, clientY: number) => boolean;
  // While the finger is down, drive the bag's screen position so it
  // visually follows the touch.
  setFingerBagPosition: (clientX: number, clientY: number) => void;
  // Reset the bag to its idle position at the bottom of the screen.
  resetFingerBagPosition: () => void;
  // The current world-space center of the finger bag. Throws use this
  // as the spawn start so flight arcs begin from where the user let go.
  getFingerBagWorldPosition: () => { x: number; y: number; z: number };
  // Show/hide the player's in-hand finger bag. Used to keep the bag
  // hidden while the OPPONENT is taking their turn — without this
  // the player's bag floats in front of the camera even when it's
  // not their move, confusing the turn order visually.
  setFingerBagVisible: (visible: boolean) => void;
  // Switch the player's perceived side of the lane. "right" = camera
  // and bag sit slightly to the right of the lane center (the
  // default); "left" = mirrored to the left. Driven by the game's
  // team1Side + the local user's team so each device renders from
  // its own assigned side.
  setPlayerSide: (side: "left" | "right") => void;
  // Which TEAM the LOCAL user is on (1 or 2). Drives which bag
  // asset the camera-parented finger bag shows — team-2 players
  // need to see THEIR bag in their hand, not team-1's. Without
  // this the finger bag is bag1 by default and a team-2 player's
  // throw appears to "swap textures" when the in-flight bag
  // correctly switches to bag2.
  setMyTeam: (team: 1 | 2) => void;
};

export function createVirtualGame(
  canvas: HTMLCanvasElement,
  loadout: GameLoadout,
  initialRegistry: AssetRegistry = BUILT_IN_REGISTRY,
): VirtualGameAPI {
  // Registry is held in a mutable shadow so the host can swap in a
  // newly-merged registry whenever React Query refetches the asset
  // catalog (e.g. admin saved alignment on the picked bag/scene).
  // Without this swap, every lookupScene/lookupBag below would
  // resolve IDs against the OLD asset objects captured at mount —
  // saved metadata would land in the DB but never reach the live
  // game until full reload.
  let registry = initialRegistry;
  // Resolve initial assets from the loadout. We keep current pointers
  // in mutable state so setLoadout can swap them later without
  // tearing down the scene.
  const current = {
    loadout,
    scene: lookupScene(registry, loadout.sceneId),
    board: lookupBoard(registry, loadout.boardId),
    bag1: lookupBag(registry, loadout.team1.bagId),
    bag2: lookupBag(registry, loadout.team2.bagId),
  };
  let dims = dimsFromAsset(current.board);

  const renderer = new WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;

  const scene = new Scene();
  let skyTex = makeSkyTexture(current.scene.palette);
  scene.background = skyTex;
  // Subtle atmospheric fog hides the ground-plane seam at the horizon.
  scene.fog = new Fog(current.scene.palette.horizon, 18, 60);

  const camera = new PerspectiveCamera(
    getTuning().cameraFov,
    canvas.clientWidth / canvas.clientHeight,
    0.1,
    100,
  );
  // Camera centered on the lane axis looking down toward the far board.
  // Lookat is pulled DOWN to the boards' high-end height so the horizon
  // sits in the upper third of the screen and the boards land closer
  // to vertical center — leaves the lower half clear lawn for the
  // swipe gesture.
  //
  // CRITICAL: both camera.x and lookAt.x are zero. Both boards live at
  // world x=0; with the camera anywhere off-axis (and lookAt centered)
  // the near board picks up a parallax shift because its z differs
  // from the lookAt target z. Centering the camera over the lane
  // guarantees both boards project to screen center regardless of
  // aspect ratio. Player-side asymmetry now lives entirely in
  // finger-bag positioning + mirroring math, not the camera rig.
  //
  // Side: which side of the lane the local player is on. Tracked as
  // metadata for the throw-mirroring code (opponent bags come from
  // the OPPOSITE side); does NOT translate the camera anymore. See
  // setPlayerSide below.
  let playerSide: "left" | "right" = "right";
  // Which team the LOCAL user is on. Drives which bag asset
  // (current.bag1 vs current.bag2) the camera-parented finger bag
  // shows. Defaults to team 1 — match createVirtualGame's pre-
  // mySide-aware behavior so a caller that never sets it (e.g. a
  // spectator) keeps the team-1 bag.
  let myTeam: 1 | 2 = 1;
  function myBag(): BagAsset {
    return myTeam === 1 ? current.bag1 : current.bag2;
  }
  function applyPlayerSideToCamera() {
    camera.position.set(0, 1.55, -0.4);
    camera.lookAt(0, getTuning().cameraLookAtY, THROW_LINE_DISTANCE);
  }
  applyPlayerSideToCamera();

  // Hemisphere fills shadows with sky color from above + ground bounce
  // from below — drastically cheaper than HDRI but reads "outdoors".
  const hemi = new HemisphereLight(
    current.scene.palette.skyLight,
    current.scene.palette.groundLight,
    getTuning().hemiIntensity,
  );
  hemi.position.set(0, 10, 0);
  scene.add(hemi);

  // A bit of ambient on top to keep the rim of the boards from going
  // pure black at low sun angles.
  const ambient = new AmbientLight(0xffffff, getTuning().ambientIntensity);
  scene.add(ambient);

  const sun = new DirectionalLight(
    current.scene.palette.sun,
    getTuning().sunIntensity,
  );
  sun.position.set(
    getTuning().sunPositionX,
    getTuning().sunPositionY,
    getTuning().sunPositionZ,
  );
  sun.castShadow = true;
  // 1024² shadow map is plenty for the cornhole footprint (boards span
  // ~3m of the lane, bags are 15cm wide) — visually indistinguishable
  // from 2048² but 4× cheaper in GPU memory + halved render cost.
  // Critical for mid-range Android performance budget.
  sun.shadow.mapSize.set(1024, 1024);
  // Slightly larger frustum than the play area itself so shadows
  // don't clip when the sun-position tuning knobs are dialed to
  // extreme angles. Sun moving far off-axis projects the play area
  // diagonally into the shadow camera's view; this margin keeps the
  // whole field covered. Resolution drops slightly across the wider
  // area, but cornhole shadow edges read fine at ~20px/m.
  sun.shadow.camera.near = 0.5;
  sun.shadow.camera.far = 60;
  sun.shadow.camera.left = -10;
  sun.shadow.camera.right = 10;
  sun.shadow.camera.top = 14;
  sun.shadow.camera.bottom = -8;
  sun.shadow.bias = -0.0005;
  // PCF soft-shadow filter is already on (renderer.shadowMap.type).
  // Small radius keeps shadow edges grounded without ugly banding.
  sun.shadow.radius = 2;
  scene.add(sun);

  // Ground material: grass scenes try to upgrade to a photographed
  // CC0 texture asynchronously. Until that loads (and for asphalt /
  // neon scenes), the procedural canvas-painted texture stands in.
  const groundMat = new MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.95,
    metalness: 0,
  });
  const ground = new Mesh(new PlaneGeometry(60, 60, 1, 1), groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  // Ground texture priority:
  //   1. bundled grass-yard.jpg — only for built-in `grass` scenes
  //   2. procedural canvas painting from the palette — otherwise
  //
  // Custom scene images (scene.skyImage) are NEVER applied to the
  // ground here. Tiling the same photo on a horizontal surface and
  // a vertical backdrop creates a clear visual split — two
  // different projections of the same image meeting at the horizon
  // line. Instead, when a custom scene is loaded the ground plane
  // is HIDDEN (visible=false) and the backdrop plane shows the
  // full image edge to edge. Bag physics still treats y=0 as
  // ground; only the visual is suppressed.
  let proceduralGroundTex: CanvasTexture | null = null;
  function applyGroundTexture(s: SceneAsset) {
    // Custom scene takes over: the backdrop plane is the entire
    // scene. Hide this opaque ground so it doesn't slice the view
    // in half with a default grass / asphalt strip below the
    // horizon. Physics doesn't care — bags collide against y=0
    // numerically.
    if (s.skyImage) {
      ground.visible = false;
      return;
    }
    ground.visible = true;

    const wantsGrass = s.palette.groundShape === "grass";

    if (proceduralGroundTex) {
      proceduralGroundTex.dispose();
      proceduralGroundTex = null;
    }
    proceduralGroundTex = makeGroundTexture(s.palette);
    groundMat.map = proceduralGroundTex;
    groundMat.needsUpdate = true;

    if (!wantsGrass) return;
    loadGroundTexture(grassYardUrl)
      .then((tex) => {
        if (current.scene.palette.groundShape !== "grass") return;
        if (current.scene.skyImage) return; // custom scene took over mid-load
        if (proceduralGroundTex) {
          proceduralGroundTex.dispose();
          proceduralGroundTex = null;
        }
        groundMat.map = tex;
        groundMat.needsUpdate = true;
      })
      .catch(() => {
        // procedural fallback already in place
      });
  }
  applyGroundTexture(current.scene);

  // ----- Scene BACKDROP plane -----
  // Flat picture-board behind the far cornhole board. Holds the
  // scene.skyImage as-is so the admin sees the literal image they
  // generated (no equirectangular pinching at the poles). Position,
  // size, and texture transform are all live-tunable from the
  // physics dock — see backdrop* fields in PhysicsTuning.
  //
  // The procedural canvas sky stays as scene.background and fills
  // the FOV edges around the plane.
  const backdropMat = new MeshBasicMaterial({
    color: 0xffffff,
    side: DoubleSide,
    transparent: false,
  });
  const backdropPlane = new Mesh(new PlaneGeometry(1, 1), backdropMat);
  backdropPlane.visible = false;
  scene.add(backdropPlane);

  function applyBackdropTransform() {
    // Per-scene backdrop framing. Each scene asset stores its own
    // distance / size / texture pan + zoom (set via the Align Scene
    // modal in /admin/design). When a field is unset on the scene
    // we fall back to a hardcoded sensible default — there's no
    // global tuning override, on purpose: scenes are too dynamic
    // for one set of knobs to fit them all.
    const s = current.scene;
    const distZ = s.backdropDistanceZ ?? 22;
    const offX = s.backdropOffsetX ?? 0;
    const offY = s.backdropOffsetY ?? 4;
    const width = s.backdropWidth ?? 50;
    // Default height 40 + offY 4 sized to cover the camera's full
    // vertical FOV at distance 22 (~y=-14..+10), so an unaligned
    // scene reads as ONE image instead of "backdrop above, blue
    // procedural sky below".
    const height = s.backdropHeight ?? 40;
    const texScale = Math.max(0.05, s.backdropScale ?? 1);
    const texU = s.backdropOffsetU ?? 0;
    const texV = s.backdropOffsetV ?? 0;
    backdropPlane.position.set(offX, offY, distZ);
    backdropPlane.scale.set(width, height, 1);
    // PlaneGeometry's front face points at +Z by default, so the plane
    // already faces the camera (camera looks in +Z). DoubleSide keeps
    // it visible even if the user drags distance behind the origin.
    const tex = backdropMat.map;
    if (tex) {
      tex.center.set(0.5, 0.5);
      tex.repeat.set(1 / texScale, 1 / texScale);
      tex.offset.set(
        0.5 - 0.5 / texScale + texU,
        0.5 - 0.5 / texScale + texV,
      );
      tex.needsUpdate = true;
    }
  }
  applyBackdropTransform();

  // Refresh the ground texture's tile-count each frame from tuning
  // so the admin can dial it live without a scene swap. Cheap — just
  // a numeric assignment to repeat.x/.y on the active texture.
  function refreshGroundTileCount() {
    const tex = groundMat.map;
    if (!tex) return;
    const n = Math.max(0.25, getTuning().groundTileCount);
    if (tex.repeat.x !== n || tex.repeat.y !== n) {
      tex.repeat.set(n, n);
      tex.needsUpdate = true;
    }
  }

  // Two boards facing each other along the lane. After flipping both 180°
  // (per gameplay feedback): far board's high end + hole sit at the FAR
  // edge with the board sloping down toward the player; near board's
  // high end is right at the player's feet, sloping up and away.
  const farBoard = createBoard(current.board, dims);
  const nearBoard = createBoard(current.board, dims);
  const boards: BoardHandle[] = [farBoard, nearBoard];
  farBoard.group.position.set(0, 0, THROW_LINE_DISTANCE);
  // farBoard: no rotation — slab high end at group +z (= world +z, away).
  nearBoard.group.position.set(0, 0, 0.8);
  nearBoard.group.rotation.y = Math.PI;
  scene.add(farBoard.group);
  scene.add(nearBoard.group);

  const bagsLayer = new Group();
  scene.add(bagsLayer);

  // ---- Score pops: a floating "+3" (etc.) that flashes by the hole
  // when a bag drops in, so a cornhole reads instantly even when the
  // bag itself is small at the far board. World-anchored sprites (not
  // a DOM overlay) so they sit in correct perspective right at the
  // hole and survive camera/FOV tuning. depthTest off → always on top.
  type ScorePop = {
    sprite: Sprite;
    mat: SpriteMaterial;
    tex: CanvasTexture;
    elapsed: number;
    ttl: number;
    baseY: number;
  };
  const scorePops: ScorePop[] = [];
  function makeScorePopTexture(text: string): CanvasTexture {
    const c = document.createElement("canvas");
    c.width = 256;
    c.height = 256;
    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, 256, 256);
    ctx.font = "900 150px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // Dark halo for contrast against any backdrop, gold fill on top.
    ctx.lineJoin = "round";
    ctx.lineWidth = 22;
    ctx.strokeStyle = "rgba(0,0,0,0.9)";
    ctx.strokeText(text, 128, 140);
    ctx.fillStyle = "#ffd23f";
    ctx.fillText(text, 128, 140);
    const tex = new CanvasTexture(c);
    tex.colorSpace = SRGBColorSpace;
    return tex;
  }
  // World position just above the far-board hole — the anchor for pops.
  function holeAnchor(): Vector3 {
    const holeLocalZ = dims.length / 2 - dims.holeFromBack;
    const surfY = farBoard.topYAtLocal(holeLocalZ);
    return new Vector3(
      farBoard.group.position.x,
      farBoard.group.position.y + surfY + 0.4,
      farBoard.group.position.z + holeLocalZ,
    );
  }
  function spawnScorePop(text: string) {
    const tex = makeScorePopTexture(text);
    const mat = new SpriteMaterial({
      map: tex,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sprite = new Sprite(mat);
    const a = holeAnchor();
    sprite.position.copy(a);
    sprite.scale.setScalar(0.05);
    sprite.renderOrder = 999;
    scene.add(sprite);
    scorePops.push({ sprite, mat, tex, elapsed: 0, ttl: 1.15, baseY: a.y });
  }
  function stepScorePops(dt: number) {
    for (let i = scorePops.length - 1; i >= 0; i--) {
      const p = scorePops[i]!;
      p.elapsed += dt;
      const u = p.elapsed / p.ttl;
      if (u >= 1) {
        scene.remove(p.sprite);
        p.mat.dispose();
        p.tex.dispose();
        scorePops.splice(i, 1);
        continue;
      }
      // Rise as it ages.
      p.sprite.position.y = p.baseY + u * 0.9;
      // Pop in with a slight overshoot, then hold size.
      const grow = u < 0.18 ? (u / 0.18) * 1.12 : 1.12 - (u - 0.18) * 0.12;
      const s = 0.85 * Math.max(0.2, grow);
      p.sprite.scale.set(s, s, 1);
      // Hold full opacity, fade out over the last 40%.
      p.mat.opacity = u < 0.6 ? 1 : Math.max(0, 1 - (u - 0.6) / 0.4);
    }
  }

  // Visible "remaining bags" piles, one per team. Both piles sit at
  // the NEAR board (player + opponent stand here in 1v1 and pitch at
  // the far board) — player's pile on the right, opponent's mirrored
  // on the left. The opponent's bag launches from above its pile so
  // the throw arcs match the player's direction (both aim at the far
  // board), just spawned from opposite sides of the foreground.
  const pile1 = new Group();
  const pile2 = new Group();
  pile1.position.set(1.45, 0, 0.05);
  pile2.position.set(-1.45, 0, 0.05);
  scene.add(pile1);
  scene.add(pile2);
  let remaining1 = 4;
  let remaining2 = 4;

  function rebuildPiles() {
    rebuildPile(pile1, remaining1, current.bag1);
    rebuildPile(pile2, remaining2, current.bag2);
  }

  rebuildPiles();

  // Pick a world-space resting target for a scripted-outcome bag.
  // Shared by throwBagScripted (when no explicit target is passed)
  // and the public pickScriptedTarget API (used by ghost dispatch
  // to commit a landing up-front so the POST stores it and every
  // remote viewer animates to the same point). Far board has no
  // Y rotation, so the local-to-world transform is just an offset
  // by its group position.
  function pickScriptedTargetWorld(
    outcome: "in_hole" | "on_board" | "miss",
  ): Vector3 {
    const boardCenter = farBoard.group.position;
    const halfL = dims.length / 2;
    const halfW = dims.width / 2;
    const holeLocalZ = halfL - dims.holeFromBack;
    const bagThickHalf = getTuning().bagThickness / 2;
    if (outcome === "in_hole") {
      const localY = farBoard.topYAtLocal(holeLocalZ);
      return new Vector3(
        boardCenter.x,
        boardCenter.y + localY + bagThickHalf,
        boardCenter.z + holeLocalZ,
      );
    }
    if (outcome === "on_board") {
      const localX = (Math.random() - 0.5) * halfW * 1.2;
      const localZ = -halfL * 0.35 + (Math.random() - 0.5) * halfL * 0.3;
      const localY = farBoard.topYAtLocal(localZ);
      return new Vector3(
        boardCenter.x + localX,
        boardCenter.y + localY + bagThickHalf,
        boardCenter.z + localZ,
      );
    }
    const sign = Math.random() < 0.5 ? -1 : 1;
    const localX = sign * (halfW + 0.4 + Math.random() * 0.3);
    const localZ = -halfL + 0.2 + Math.random() * 0.6;
    return new Vector3(
      boardCenter.x + localX,
      bagThickHalf,
      boardCenter.z + localZ,
    );
  }

  // Camera is added to the scene so camera-parented children (the
  // finger-throw bag) render. The rig system used to live here — see
  // git history if you need the old player/opponent arm code.
  scene.add(camera);
  // Tracks whether the player has a bag in hand this beat. Driven by
  // grabBag / throwBag so future visuals (a small chip, a pile dec, a
  // bag-in-pocket indicator) have a stable signal to read.
  let playerHasBag = true;

  // ----- Finger-throw mode -----
  // Big bag at the bottom of the screen the user touches + flicks
  // directly. Camera-parented so it stays put regardless of where the
  // camera looks. Mesh is rebuilt whenever the picked bag changes,
  // so the in-hand visual matches the bag picker.
  let fingerBag: Mesh | null = null;
  // The React layer (setFingerBagVisible, driven by canPlayerThrow)
  // is the SINGLE owner of whether the in-hand bag should be shown.
  // The renderer caches that intent here so any renderer-internal
  // visibility toggle (e.g. the post-throw show-again timer) restores
  // to the LAST REQUESTED state instead of unconditionally re-showing
  // — which used to pop a phantom bag back into the hand after the
  // player's final throw of a round, before the ceremony.
  let fingerBagWanted = false;
  function disposeFingerBag() {
    if (!fingerBag) return;
    camera.remove(fingerBag);
    disposeObject(fingerBag);
    fingerBag = null;
  }
  function rebuildFingerBag(bagAsset: BagAsset): void {
    disposeFingerBag();
    // Imaged bags wrap one material across all faces (UV mapping
    // projects every vertex onto a top-down plane; sides + bevels
    // sample the image's edge pixels, which encode the bag's body
    // fabric color). Color-only bags use the two-material split so
    // sides can be a touch rougher than the cap.
    let mesh: Mesh;
    if (bagAsset.topImage) {
      const mat = new MeshStandardMaterial({
        color: 0xffffff,
        roughness: bagAsset.roughness,
      });
      applyFabricFinish(mat);
      loadImageTexture(bagAsset.topImage).then((tex) => {
        if (!tex) return;
        applyBagTextureTransform(tex, bagAsset);
        mat.map = tex;
        mat.needsUpdate = true;
      });
      const tFb = getTuning();
      mesh = new Mesh(makeBagGeometry(tFb.bagSize, tFb.bagThickness), mat);
    } else {
      const topMat = new MeshStandardMaterial({
        color: bagAsset.color,
        roughness: bagAsset.roughness,
      });
      const sideMat = new MeshStandardMaterial({
        color: bagAsset.color,
        roughness: Math.min(1, bagAsset.roughness + 0.03),
      });
      applyFabricFinish(topMat);
      applyFabricFinish(sideMat);
      const tFb = getTuning();
      mesh = new Mesh(
        makeBagGeometry(tFb.bagSize, tFb.bagThickness),
        [topMat, sideMat],
      );
    }
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    // Start HIDDEN. The page-level effect that calls
    // setFingerBagVisible(true) only fires when canPlayerThrow flips
    // true; if the renderer mounts AFTER that effect's first run
    // (the canvas + asset load can lag a snapshot/SSE round-trip),
    // the effect's `apiRef.current?.` no-ops silently and the bag
    // would otherwise stay at Three.js's default `visible: true` —
    // showing for non-starter players who never legitimately get a
    // setFingerBagVisible(true) call. Starting hidden makes the
    // visibility gate explicit: only show after a caller turns it on.
    mesh.visible = false;
    // Rotation is driven by tuning (see applyFingerBagTransform).
    camera.add(mesh);
    fingerBag = mesh;
    applyFingerBagTransform();
  }
  // Lateral offset that puts the player's bag in the user's hand.
  // Used to flip sign with playerSide so the bag visually sat on the
  // user's "lane side", but that made tuning game-dependent: a player
  // who tuned the bag position in a ghost game (team1Side default
  // "right") saw it FLIP to the opposite side the moment the server
  // randomly assigned them to the left in a 1v1. Every player now
  // sees their own bag in the same screen position — fingerBagOffsetX
  // is the single tunable for lateral placement; PLAYER_SIDE_X is the
  // baked-in default offset (~0.3m to the right of camera center).
  // The NDC mirror used by throwBagScripted automatically places the
  // opponent's bag at the mirror world position, so every player's
  // POV shows themselves on the right and opponent on the left
  // regardless of who's the "left side" in the underlying game.
  const PLAYER_SIDE_X = 0.3;
  function applyFingerBagTransform() {
    if (!fingerBag) return;
    const t = getTuning();
    fingerBag.position.set(
      t.fingerBagOffsetX + PLAYER_SIDE_X,
      t.fingerBagOffsetY,
      t.fingerBagOffsetZ,
    );
    fingerBag.scale.setScalar(t.fingerBagScale);
    // Rotation from tuning (degrees → radians). Pitch tilts the bag
    // top toward camera, yaw spins horizontally, roll twists in-plane.
    const d2r = Math.PI / 180;
    fingerBag.rotation.set(
      t.fingerBagRotX * d2r,
      t.fingerBagRotY * d2r,
      t.fingerBagRotZ * d2r,
    );
    // Note: the bag's texture transform (offset/scale/rotation that
    // crops the generated image to fit the cap face) lives on the
    // BagAsset itself and is applied once when the texture loads via
    // applyBagTextureTransform — see /admin/design's alignment tool.
  }
  rebuildFingerBag(myBag());

  let raf = 0;
  let last = performance.now();
  function loop(now: number) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    for (const bag of activeBags) bag.step(dt);
    stepScorePops(dt);
    // Re-apply the finger-bag transform every frame so the in-game
    // tuner can nudge position / scale / mode live without restart.
    applyFingerBagTransform();
    // Same idea for the scene backdrop — admin dials distance, size,
    // and texture pan/zoom from the dock while watching the result.
    applyBackdropTransform();
    refreshGroundTileCount();
    renderer.render(scene, camera);
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);

  function onResize() {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  window.addEventListener("resize", onResize);

  // Live tuning: re-apply the values that don't auto-pickup each frame
  // (camera lookAt, light intensities) whenever the admin tuner mutates
  // the singleton. Per-frame physics values are read inside their hot
  // paths so they don't need a subscription.
  const unsubscribeTuning = subscribeTuning(() => {
    const t = getTuning();
    // FOV change requires updateProjectionMatrix() — otherwise the
    // camera's internal matrix keeps the stale FOV value and nothing
    // visually changes.
    if (camera.fov !== t.cameraFov) {
      camera.fov = t.cameraFov;
      camera.updateProjectionMatrix();
    }
    // Re-apply side + lookAt together so changes to cameraLookAtY pick
    // up the correct camera X offset for the current side.
    applyPlayerSideToCamera();
    hemi.intensity = t.hemiIntensity;
    ambient.intensity = t.ambientIntensity;
    sun.intensity = t.sunIntensity;
    // Move the sun. The directional light's shadow camera follows
    // light.position automatically each frame, so shadows update
    // live — no shadow.needsUpdate flag required.
    sun.position.set(t.sunPositionX, t.sunPositionY, t.sunPositionZ);
  });

  const activeBags: BagBody[] = [];

  function applyScene(s: SceneAsset) {
    const p = s.palette;
    // Paint the procedural sky immediately so first frame isn't blank.
    skyTex.dispose();
    skyTex = makeSkyTexture(p);
    scene.background = skyTex;
    (scene.fog as Fog).color.set(p.horizon);
    applyGroundTexture(s);
    hemi.color.set(p.skyLight);
    hemi.groundColor.set(p.groundLight);
    sun.color.set(p.sun);
    boards.forEach((b) => b.repaint(p, current.board));

    // Scene image goes on the flat backdrop plane, NOT as an
    // equirect background — equirect mapping pinches normal photos
    // at the poles and the admin couldn't tell what they generated.
    // The plane shows the image at its true perspective; the
    // procedural canvas sky still fills the FOV edges around it.
    if (s.skyImage) {
      const wantedUrl = s.skyImage;
      loadImageTexture(wantedUrl).then((tex) => {
        if (!tex) return;
        if (current.scene.skyImage !== wantedUrl) return; // user swapped scenes mid-load
        tex.colorSpace = SRGBColorSpace;
        // Texture transform is applied each frame in applyBackdropTransform
        // from the live tuning knobs (offset/scale/rotation).
        backdropMat.map = tex;
        backdropMat.needsUpdate = true;
        backdropPlane.visible = true;
        applyBackdropTransform();
      });
    } else {
      // No custom image — hide the plane so we see procedural sky.
      if (backdropMat.map) {
        backdropMat.map.dispose();
        backdropMat.map = null;
        backdropMat.needsUpdate = true;
      }
      backdropPlane.visible = false;
    }
  }

  // Fire the initial scene application now that everything's set up.
  // Without this the backdrop plane stays invisible (its .visible
  // default is false and applyScene is the only thing that toggles
  // it on), so the first render of the picked scene shows just the
  // procedural sky + boards floating in mid-air until the user
  // happens to pick a different scene and trigger setLoadout's
  // applyScene branch. Repeats some work applyGroundTexture already
  // did at construction but keeps the boot path simple.
  applyScene(current.scene);

  return {
    setRegistry(next) {
      registry = next;
    },
    setLoadout(next) {
      current.loadout = next;
      // Resolve each slot. Anything that changed gets rebuilt; anything
      // that didn't is a no-op.
      const newScene = lookupScene(registry, next.sceneId);
      const newBoard = lookupBoard(registry, next.boardId);
      const newBag1 = lookupBag(registry, next.team1.bagId);
      const newBag2 = lookupBag(registry, next.team2.bagId);
      const sceneChanged = newScene.id !== current.scene.id;
      const boardChanged = newBoard.id !== current.board.id;
      // Detect bag swaps AND in-place metadata edits (e.g. admin
      // saved a new alignment in /admin/design's Align tool — same
      // bag id, different texOffset / texScale / texRotationDeg). An
      // id-only check would let the renderer keep the stale texture
      // transform until full reload, which is exactly the "I aligned
      // it but the bag doesn't show it" bug we're fixing.
      const bag1Changed = bagChanged(current.bag1, newBag1);
      const bag2Changed = bagChanged(current.bag2, newBag2);
      const bagsChanged = bag1Changed || bag2Changed;
      current.scene = newScene;
      current.board = newBoard;
      current.bag1 = newBag1;
      current.bag2 = newBag2;
      if (boardChanged) {
        dims = dimsFromAsset(newBoard);
        // Board geometry swap would require rebuilding the two BoardHandles.
        // Out-of-scope for this pass (we ship a single board asset today)
        // but the API is ready: callers calling setLoadout with a
        // different boardId will hit this and we'll grow into it.
      }
      if (sceneChanged || boardChanged) applyScene(newScene);
      // Finger-throw mode mirrors the picker: the bag at the bottom
      // of the screen swaps to whatever the picker for the LOCAL
      // user's team just landed on. Spectators / pre-mySide callers
      // keep team-1's bag (myTeam defaults to 1).
      const myBagChanged =
        myTeam === 1 ? bag1Changed : bag2Changed;
      if (myBagChanged) {
        rebuildFingerBag(myBag());
      }
      if (sceneChanged || bagsChanged || boardChanged) rebuildPiles();
    },
    setScene(id) {
      this.setLoadout({ ...current.loadout, sceneId: id });
    },
    setBagsRemaining(t1, t2) {
      remaining1 = Math.max(0, Math.min(4, t1));
      remaining2 = Math.max(0, Math.min(4, t2));
      rebuildPiles();
    },
    setActiveTeam(team) {
      // No-op stub. The rig system used to swap player / opponent arm
      // visibility here; with finger-throw mode shipping solo and the
      // opponent already on "magic launch" (no rig), there's nothing
      // visual to drive per-team yet. Kept in the API so VirtualPlay
      // can invoke it every turn-switch without a guard, and so a
      // future turn-indicator (e.g. "your turn" overlay) has a home.
      void team;
      void playerHasBag;
    },
    async grabBag(team) {
      // Visual flourish used to live here when the player rig played a
      // reach-and-grab animation. With the rig gone the only state we
      // still flip is the in-hand flag — the round-state bookkeeping
      // (pile count decrement, swipe→throw) still lives in VirtualPlay.
      if (team !== 1) return;
      playerHasBag = true;
    },
    async throwBag(input) {
      const tune = getTuning();
      // Spawn point.
      //   Team 1 (player): caller's `start` wins (finger-throw mode
      //     passes the bag's screen position so the arc fires from
      //     where the user let go). Otherwise default to a chest-
      //     height fixed point in front of the camera.
      //   Team 2 (opponent): magic launch — bag pops in above the
      //     opponent's pile at the NEAR board (left-side mirror of
      //     where the player throws from) and arcs forward toward
      //     the FAR board, exactly like the player. No rig flourish;
      //     the start position is overridden ignoring whatever the
      //     caller sent.
      let start: Vector3;
      if (input.team === 2) {
        // Mirror the player's launch across the camera's centerline
        // so opponent throws come from the LEFT and player's come
        // from the RIGHT (whichever side the finger bag is offset
        // to). Sourcing from the finger bag's live world position
        // keeps player and opponent symmetric even when the admin
        // nudges the bag with the tuner.
        const playerWorld = new Vector3();
        if (fingerBag) {
          fingerBag.getWorldPosition(playerWorld);
        } else {
          // Race on first frame before fingerBag is mounted — fall
          // back to a sane left-of-camera spot.
          playerWorld.set(0, 1.55, 0);
        }
        start = new Vector3(-playerWorld.x, playerWorld.y, playerWorld.z);
      } else if (input.start) {
        start = new Vector3(input.start.x, input.start.y, input.start.z);
      } else {
        start = new Vector3(input.dirX * 0.2, 1.55, 0);
      }
      // Finger-throw: briefly hide the camera-anchored bag so the
      // flying mesh doesn't visually stack on top of it. After the
      // flight clears frame, reset its idle pose and restore the bag
      // to whatever the React layer LAST REQUESTED (fingerBagWanted),
      // NOT unconditionally visible. Forcing it visible here re-showed
      // a phantom bag after the player's final throw — the round was
      // over (bagsRemaining=0 → canPlayerThrow=false → React asked for
      // hidden), but this timer overrode that and popped a 5th bag
      // into the hand before the ceremony. Respecting fingerBagWanted
      // keeps the React visibility gate authoritative.
      if (fingerBag && input.start) {
        fingerBag.visible = false;
        setTimeout(() => {
          if (!fingerBag) return;
          applyFingerBagTransform();
          fingerBag.visible = fingerBagWanted;
        }, 700);
      }
      // Ideal aim point: a fraction of the slab length from the
      // front-low edge (default 0.166 = front 1/3 sweet spot).
      const farBoardCenterZ = farBoard.group.position.z;
      const frontEdgeZ = farBoardCenterZ - dims.length / 2;
      const idealTargetZ = frontEdgeZ + dims.length * tune.targetZFraction;
      const idealTargetY =
        dims.frontLift + Math.sin(dims.rake) * (dims.length * tune.targetZFraction);
      // Normalize swipe power into 0..1. powerMaxMs is the slowest
      // swipe that still counts; powerMinMs is the fastest.
      const clampedMs =
        Math.min(Math.max(input.powerMs, tune.powerMinMs), tune.powerMaxMs);
      const power =
        1 -
        (clampedMs - tune.powerMinMs) /
          Math.max(1, tune.powerMaxMs - tune.powerMinMs);
      // Distance scaling: power maps onto a ramp between two ends.
      //   power=0 → floor × (start → ideal target) distance
      //   power=1 → ceiling × (start → ideal target) distance
      // Ceiling > 1.0 lets a hard flick fly PAST the board (overshoot
      // long); floor < 0.85 makes a weak swipe land short of the slab.
      // The player's personal power slider then multiplies the WHOLE
      // ramp — applied AFTER the flick-derived scale and NOT re-clamped
      // to the ceiling — so a 1.4× slider on a max flick clears the
      // board (real overshoot) instead of saturating at the ceiling
      // the way a normPower multiplier did.
      const distanceScale =
        (tune.powerDistanceFloor +
          (tune.powerDistanceCeiling - tune.powerDistanceFloor) * power) *
        (input.powerScale ?? 1);
      const aimZ = start.z + (idealTargetZ - start.z) * distanceScale;
      // Y target only counts as "on the slab" when the throw scale is
      // close enough to actually reach it; short throws aim for ground.
      const aimY = distanceScale >= 0.85 ? idealTargetY : 0;
      const flightTime = tune.flightTimeBase - tune.flightTimeFactor * power;
      const vy =
        (aimY +
          tune.vyLookahead -
          start.y -
          0.5 * tune.gravity * flightTime * flightTime) /
          flightTime +
        input.dirY * tune.vyDirYFactor;
      const vz =
        (aimZ - start.z + input.dirX * tune.vzDirXFactor) / flightTime;
      const vx = -input.dirX * tune.vxFactor;
      const bagAsset = input.team === 1 ? current.bag1 : current.bag2;
      const bag = createBag(
        start,
        new Vector3(vx, vy, vz),
        bagAsset,
        dims,
        activeBags,
        null,
        () => spawnScorePop("+3"),
      );
      bagsLayer.add(bag.mesh);
      activeBags.push(bag);
      if (input.team === 1) playerHasBag = false;
      // No scale override needed — createBag now builds at
      // fingerBagScale, the single shared bag-size knob, so the
      // just-released bag is exactly the size of the in-hand finger
      // bag, the pile bags, and the opponent's bag.
      const result = await bag.simulate(farBoard);
      // Don't splice — settled bags stay in activeBags so subsequent
      // sliding throws can collide with them. resetBoard() clears the
      // whole pool at the end of each round.
      return result;
    },
    pickScriptedTarget(_team, outcome) {
      const t = pickScriptedTargetWorld(outcome);
      return { x: t.x, y: t.y, z: t.z };
    },
    async throwBagScripted(team, outcome, opts) {
      // Programmatic throw with a forced outcome. The bag still flies
      // on a parabolic arc (so it looks like a real throw), but the
      // landing point and final result are predetermined — no physics
      // drift, no chance of "ghost-4 only scored 3".
      const tune = getTuning();
      const holdSeconds = opts?.holdSeconds ?? 0;

      // Spawn point: mirror in SCREEN space across the near board's
      // projected centerline. Not in world space.
      //
      // World-space X-flip (the obvious approach) only works when the
      // player's camera sits on the lane centerline. With the player
      // camera offset for side play, world X=0 projects to a screen
      // position OFF the screen center. A world X-flip then drops the
      // opponent's bag far to the side of its projected board center —
      // visually nowhere near symmetric, even though it's geometrically
      // symmetric in the world.
      //
      // The user perceives "mirrored across the board," meaning the
      // VISUAL distance from the board's center to each bag should
      // match on screen. So compute that directly:
      //
      //   1. Project player bag's world position to NDC (screen-space
      //      normalized [-1, +1] coordinates).
      //   2. Project the near board's center to NDC.
      //   3. Reflect player NDC.x across board NDC.x → opponent NDC.x.
      //      Keep NDC.y (same hand height on screen) and NDC.z (same
      //      camera depth) so the opponent's bag lands at the same
      //      pixel height and the same perspective distance.
      //   4. Unproject the resulting NDC back to a world position.
      //
      // Result: the opponent's bag is at a WORLD location whose
      // projection to THIS camera produces a screen point that is
      // pixel-symmetric to the player's bag across the visible
      // board. Camera side-offset, FOV changes, lookAt tweaks — all
      // automatically handled because the mirror lives in NDC.
      //
      // Scripted throws are always OPPONENT throws (local user's
      // own throws go through throwBag with input.start), so the
      // `team` parameter only picks which bag asset to use; never
      // which side. Mirror logic applies to both teams uniformly.
      const playerWorld = new Vector3();
      if (fingerBag) {
        fingerBag.getWorldPosition(playerWorld);
      } else {
        playerWorld.set(0, 1.55, 0);
      }
      // Ensure the camera's projection matrices are up to date —
      // FOV / position changes from live tuning need to be flushed
      // before project() / unproject() run.
      camera.updateMatrixWorld(true);
      camera.updateProjectionMatrix();
      const playerNDC = playerWorld.clone().project(camera);
      const boardNDC = nearBoard.group.position.clone().project(camera);
      const oppNDC = new Vector3(
        2 * boardNDC.x - playerNDC.x,
        playerNDC.y,
        playerNDC.z,
      );
      const startVec = oppNDC.unproject(camera);

      // Either use the explicit target passed in (multiplayer/ghost
      // dispatch picks it up-front, POSTs it, and every device
      // animates to the SAME point) or pick a fresh one from the
      // outcome zone.
      const target = opts?.target
        ? new Vector3(opts.target.x, opts.target.y, opts.target.z)
        : pickScriptedTargetWorld(outcome);
      // For in_hole, force the arc target Y onto the slab SURFACE above
      // the hole. The stored landing can be the hole BOTTOM (y≈0, from a
      // player's real-physics 3-pointer) which would arc the bag under
      // the deck and make it vanish; scriptedStep then drops it into the
      // throat from the surface for a visible cornhole. Keep x/z as
      // stored so every device lands at the same spot.
      if (outcome === "in_hole") {
        const localZ = target.z - farBoard.group.position.z;
        target.y =
          farBoard.group.position.y +
          farBoard.topYAtLocal(localZ) +
          getTuning().bagThickness / 2;
      }

      // Texture/color from the actual throwing team's bag asset; bag
      // GEOMETRY comes from global PhysicsTuning (bagSize +
      // bagThickness) — see createBag, where it reads tuning directly.
      // That keeps every bag in the world the same physical size
      // regardless of which team picked which asset.
      const bagAsset = team === 1 ? current.bag1 : current.bag2;
      const bag = createBag(
        startVec,
        new Vector3(0, 0, 0), // velocity ignored — scripted path drives position
        bagAsset,
        dims,
        activeBags,
        {
          target,
          outcome,
          flightTime: tune.flightTimeBase,
          holdSeconds,
        },
        () => spawnScorePop("+3"),
      );
      bagsLayer.add(bag.mesh);
      activeBags.push(bag);
      // Mirror the finger bag's WORLD orientation onto the opponent's
      // bag at the hold position so it reads as a true mirror image —
      // same tilt. Without this the opponent's rotation is just random
      // yaw (flat-top) while the finger bag is tilted to a held pose,
      // so the silhouettes don't match and the opponent looks oversized
      // (we'd see its full top face).
      //
      // Quaternion mirror across YZ plane: negate y and z components.
      // Derivation: for a rotation R around axis n by angle θ, the
      // mirror M*R*M (M = diag(-1, 1, 1)) is a rotation around
      // (M·n) with the same angle. In quaternion form this flips
      // the y and z imaginary components.
      //
      // Size is NOT copied here anymore — createBag already builds the
      // bag at fingerBagScale (the single shared bag-size knob), so the
      // opponent bag matches the finger bag exactly. The old
      // scale.copy was futile anyway: the per-frame squash integrator
      // overwrote it.
      if (fingerBag) {
        fingerBag.updateMatrixWorld();
        const fbWorld = new Quaternion();
        fingerBag.getWorldQuaternion(fbWorld);
        bag.mesh.quaternion.set(fbWorld.x, -fbWorld.y, -fbWorld.z, fbWorld.w);
      }
      const result = await bag.simulate(farBoard);
      return result;
    },
    resetBoard() {
      while (bagsLayer.children.length) {
        const c = bagsLayer.children[0]!;
        bagsLayer.remove(c);
        disposeObject(c);
      }
      activeBags.length = 0;
    },
    destroy() {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      unsubscribeTuning();
      renderer.dispose();
      disposeFingerBag();
      for (const layer of [bagsLayer, pile1, pile2]) {
        while (layer.children.length) {
          const c = layer.children[0]!;
          layer.remove(c);
          disposeObject(c);
        }
      }
    },

    // ----- Finger-throw API -----
    isFingerBagHit(clientX, clientY) {
      if (!fingerBag || !fingerBag.visible) return false;
      // Project the bag's world position to NDC, then to pixel space,
      // and compare against the touch position with a generous radius
      // (touch targets need slack for fingertips). Bag is camera-
      // parented so its world position updates with the camera.
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const worldPos = new Vector3();
      fingerBag.getWorldPosition(worldPos);
      const ndc = worldPos.clone().project(camera);
      const screenX = (ndc.x + 1) * 0.5 * w;
      const screenY = (1 - (ndc.y + 1) * 0.5) * h;
      // Hit radius scales with viewport so a phone tap and a desktop
      // click both feel reasonable. ~22% of the shorter dim by default.
      const radiusPx = Math.min(w, h) * 0.22 * getTuning().fingerBagScale;
      const dx = clientX - screenX;
      const dy = clientY - screenY;
      return dx * dx + dy * dy <= radiusPx * radiusPx;
    },
    setFingerBagPosition(clientX, clientY) {
      if (!fingerBag) return;
      // Convert pointer screen pos to camera-local position at the
      // bag's depth so the bag visually tracks the finger.
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const ndcX = (clientX / w) * 2 - 1;
      const ndcY = -((clientY / h) * 2 - 1);
      const t = getTuning();
      const fovRad = (camera.fov * Math.PI) / 180;
      const halfH = Math.tan(fovRad / 2) * Math.abs(t.fingerBagOffsetZ);
      const halfW = halfH * camera.aspect;
      fingerBag.position.set(ndcX * halfW, ndcY * halfH, t.fingerBagOffsetZ);
    },
    resetFingerBagPosition() {
      applyFingerBagTransform();
    },
    getFingerBagWorldPosition() {
      const out = new Vector3();
      if (!fingerBag) {
        // Fallback to a sensible default if for some reason the bag
        // isn't mounted yet (race on first frame).
        const t = getTuning();
        out.set(t.fingerBagOffsetX, t.fingerBagOffsetY, t.fingerBagOffsetZ);
        out.applyMatrix4(camera.matrixWorld);
      } else {
        fingerBag.getWorldPosition(out);
      }
      return { x: out.x, y: out.y, z: out.z };
    },
    setFingerBagVisible(visible) {
      // Record the React layer's intent so renderer-internal toggles
      // (post-throw timer) restore to it instead of forcing visible.
      fingerBagWanted = visible;
      if (fingerBag) fingerBag.visible = visible;
    },
    setMyTeam(team) {
      if (myTeam === team) return;
      myTeam = team;
      // Force a rebuild even if the underlying bag asset reference
      // is the same — we're swapping FROM bag1 to bag2 (or vice
      // versa), which is a different material + UV mapping.
      rebuildFingerBag(myBag());
    },
    setPlayerSide(side) {
      playerSide = side;
      applyPlayerSideToCamera();
      applyFingerBagTransform();
    },
  };
}

type Outcome = ThrowResult["outcome"];

export type BoardHandle = {
  group: Group;
  // Returns the world-space top-surface point at the given local (x, z),
  // accounting for rake + rotation. Used by physics to know where the
  // slab is in world space.
  topYAtLocal: (localZ: number) => number;
  // Outcome of a bag at this resting world position. Handles the board's
  // rotation around Y so the hole sits in the correct world-space spot.
  outcomeAt: (resting: { x: number; y: number; z: number }) => Outcome;
  repaint: (palette: ScenePalette, board?: BoardAsset) => void;
};

// R2 originally served textures without CORS headers, and the responses
// carry `cache-control: public, max-age=31536000, immutable`. Mobile
// browsers (and any client that loaded a texture before the CORS policy
// was applied) have the no-CORS response pinned for a year — a normal
// refresh re-uses it and three.js's crossOrigin='anonymous' load fails
// silently. Appending a versioned query string forces a fresh cache key,
// so the next fetch goes back to R2 and picks up the CORS-tagged
// response. Bump TEXTURE_CACHE_BUST whenever you need to invalidate
// every client's cached texture (CORS rollout, content rewrite, etc.).
const TEXTURE_CACHE_BUST = "2";
function withBust(url: string): string {
  // Don't touch data: URIs, blob: URLs, or anything that already has a
  // query string we shouldn't clobber. The only URLs we need to bust
  // are R2 GETs, which don't have one.
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.includes("?")) return url;
  return `${url}?v=${TEXTURE_CACHE_BUST}`;
}

// Ground-texture loader. Default: bundled Poly Haven CC0 grass (a
// Vite-imported, content-hashed asset URL). When the active scene
// supplies its own `groundImage` (e.g. an admin-generated R2 URL) the
// caller passes it in and we load that instead. Cached per-URL so a
// scene swap doesn't re-fetch.
const groundTexCache = new Map<string, Texture>();
const groundTexLoading = new Map<string, Promise<Texture>>();
function loadGroundTexture(url: string = grassYardUrl): Promise<Texture> {
  const bustedUrl = withBust(url);
  const cached = groundTexCache.get(bustedUrl);
  if (cached) return Promise.resolve(cached);
  const inflight = groundTexLoading.get(bustedUrl);
  if (inflight) return inflight;
  const p = new Promise<Texture>((resolve, reject) => {
    new TextureLoader().load(
      bustedUrl,
      (tex) => {
        tex.wrapS = RepeatWrapping;
        tex.wrapT = RepeatWrapping;
        // Tile count is set on the SHARED instance whenever it's
        // used by the ground material — see applyGroundTexture which
        // refreshes repeat from the live tuning each frame the
        // texture is bound.
        tex.repeat.set(20, 20);
        tex.anisotropy = 16;
        tex.colorSpace = SRGBColorSpace;
        groundTexCache.set(bustedUrl, tex);
        resolve(tex);
      },
      undefined,
      (e) => {
        // On failure (404, CORS, etc.) clear the in-flight marker so a
        // retry can re-attempt and fall back to the bundled grass URL.
        groundTexLoading.delete(bustedUrl);
        reject(e);
      },
    );
  });
  groundTexLoading.set(bustedUrl, p);
  return p;
}

// Generic image texture cache, keyed by URL. Used by boards + bags
// when an asset specifies a `topImage` — we kick off the load
// asynchronously and the caller can swap the texture into its
// material when the promise resolves. Failures resolve to null so the
// caller stays on its procedural fallback rather than throwing.
const imageTexCache = new Map<string, Texture>();
const imageTexLoading = new Map<string, Promise<Texture | null>>();
export function loadImageTexture(url: string): Promise<Texture | null> {
  // Cache-bust through stale no-CORS responses pinned by R2's
  // immutable/1-year max-age. See withBust + TEXTURE_CACHE_BUST.
  const bustedUrl = withBust(url);
  const cached = imageTexCache.get(bustedUrl);
  if (cached) return Promise.resolve(cached);
  const inflight = imageTexLoading.get(bustedUrl);
  if (inflight) return inflight;
  const p = new Promise<Texture | null>((resolve) => {
    const loader = new TextureLoader();
    // Mobile WebGL fails silently on non-power-of-two textures with
    // default mipmap generation + repeat wrapping. Generated images
    // (Gemini → R2) come in arbitrary dimensions, so disable mipmaps
    // up front and use linear filtering. Desktop loses a tiny bit of
    // minification quality but mobile actually displays the texture.
    loader.load(
      bustedUrl,
      (tex) => {
        tex.colorSpace = SRGBColorSpace;
        tex.anisotropy = 4;
        tex.generateMipmaps = false;
        tex.minFilter = LinearFilter;
        tex.magFilter = LinearFilter;
        imageTexCache.set(bustedUrl, tex);
        resolve(tex);
      },
      undefined,
      () => resolve(null),
    );
  });
  imageTexLoading.set(bustedUrl, p);
  return p;
}

// Detect whether a loaded texture's source image carries real alpha
// (i.e. it's a transparent PNG, not a JPEG / studio-white background).
// Sniff by drawing the image to a small offscreen canvas and sampling
// a sparse grid for any non-opaque pixel. Cheap (one canvas, ~10×10
// samples) and cached on the texture so repeated lookups are free.
const alphaSniffCache = new WeakMap<Texture, boolean>();
function imageHasAlpha(tex: Texture): boolean {
  const cached = alphaSniffCache.get(tex);
  if (cached !== undefined) return cached;
  const img = tex.image as HTMLImageElement | HTMLCanvasElement | undefined;
  if (!img || !("width" in img) || !img.width) {
    alphaSniffCache.set(tex, false);
    return false;
  }
  try {
    const c = document.createElement("canvas");
    const W = 32;
    const H = 32;
    c.width = W;
    c.height = H;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      alphaSniffCache.set(tex, false);
      return false;
    }
    ctx.drawImage(img, 0, 0, W, H);
    const data = ctx.getImageData(0, 0, W, H).data;
    // Any pixel with alpha noticeably below 255 means the image
    // carries real transparency. Use 250 as the threshold to ignore
    // JPEG-compression alpha noise.
    for (let i = 3; i < data.length; i += 4) {
      if (data[i]! < 250) {
        alphaSniffCache.set(tex, true);
        return true;
      }
    }
    alphaSniffCache.set(tex, false);
    return false;
  } catch {
    // Cross-origin reads can throw on getImageData if CORS headers
    // aren't tagged — but we set crossOrigin via TextureLoader.load,
    // so this is a defensive fallback. Treat as "no alpha" to keep
    // the existing white-key behavior.
    alphaSniffCache.set(tex, false);
    return false;
  }
}

// Sky background: a vertical gradient from zenith → horizon. Rendered
// via equirectangular mapping so it surrounds the camera correctly
// rather than stretching across a single quad.
function makeSkyTexture(p: ScenePalette): CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 32;
  c.height = 256;
  const ctx = c.getContext("2d")!;
  const grad = ctx.createLinearGradient(0, 0, 0, c.height);
  grad.addColorStop(0, "#" + p.zenith.toString(16).padStart(6, "0"));
  grad.addColorStop(0.65, blendHex(p.zenith, p.horizon, 0.6));
  grad.addColorStop(1, "#" + p.horizon.toString(16).padStart(6, "0"));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, c.width, c.height);
  // A little high-frequency cloud band near the top for non-uniform sky.
  for (let i = 0; i < 18; i++) {
    const y = Math.random() * c.height * 0.55;
    const w = c.width;
    const h = 2 + Math.random() * 6;
    ctx.fillStyle = `rgba(255,255,255,${0.04 + Math.random() * 0.05})`;
    ctx.fillRect(0, y, w, h);
  }
  const tex = new CanvasTexture(c);
  tex.mapping = EquirectangularReflectionMapping;
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

// Ground: a hand-painted grass texture with individual blade strokes, a
// few dirt patches, and color variance. Tiles across the ground plane.
function makeGroundTexture(p: ScenePalette): CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 1024;
  const ctx = c.getContext("2d")!;

  if (p.groundShape === "asphalt") {
    // Pebble-grit asphalt: dark base with light speckle.
    ctx.fillStyle = "#" + p.groundBase.toString(16).padStart(6, "0");
    ctx.fillRect(0, 0, c.width, c.height);
    for (let i = 0; i < 9000; i++) {
      const x = Math.random() * c.width;
      const y = Math.random() * c.height;
      const r = 0.4 + Math.random() * 1.8;
      const t = Math.random();
      ctx.fillStyle =
        t < 0.6
          ? `rgba(255,255,255,${0.04 + Math.random() * 0.06})`
          : `rgba(0,0,0,${0.05 + Math.random() * 0.1})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (p.groundShape === "neon") {
    // Glossy neon floor: dark base + faint grid lines.
    ctx.fillStyle = "#" + p.groundBase.toString(16).padStart(6, "0");
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "rgba(180,80,220,0.22)";
    ctx.lineWidth = 1;
    const step = 64;
    for (let i = 0; i <= c.width; i += step) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, c.height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(c.width, i);
      ctx.stroke();
    }
  } else {
    // Grass: blotchy base, thousands of tiny blade strokes, occasional dirt.
    const baseHex = "#" + p.groundBase.toString(16).padStart(6, "0");
    ctx.fillStyle = baseHex;
    ctx.fillRect(0, 0, c.width, c.height);
    // Color variance to break up the flatness.
    for (let i = 0; i < 1400; i++) {
      const x = Math.random() * c.width;
      const y = Math.random() * c.height;
      const r = 6 + Math.random() * 22;
      const mix = Math.random();
      ctx.fillStyle = `rgba(${(p.groundAccent >> 16) & 0xff},${
        (p.groundAccent >> 8) & 0xff
      },${p.groundAccent & 0xff},${0.06 + mix * 0.18})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Grass blades — short upward strokes.
    for (let i = 0; i < 8500; i++) {
      const x = Math.random() * c.width;
      const y = Math.random() * c.height;
      const v = Math.random();
      const r = 40 + v * 90;
      const g = 90 + v * 110;
      const b = 30 + v * 50;
      ctx.strokeStyle = `rgba(${r | 0},${g | 0},${b | 0},${0.55 + Math.random() * 0.4})`;
      ctx.lineWidth = 0.5 + Math.random() * 0.8;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + (Math.random() - 0.5) * 1.5, y - (3 + Math.random() * 4));
      ctx.stroke();
    }
    // Dirt patches.
    const dirtHex = `rgba(${(p.groundDirt >> 16) & 0xff},${
      (p.groundDirt >> 8) & 0xff
    },${p.groundDirt & 0xff},`;
    for (let i = 0; i < 22; i++) {
      const x = Math.random() * c.width;
      const y = Math.random() * c.height;
      const rx = 10 + Math.random() * 22;
      const ry = rx * (0.6 + Math.random() * 0.4);
      ctx.fillStyle = `${dirtHex}${0.15 + Math.random() * 0.25})`;
      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, Math.random() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const tex = new CanvasTexture(c);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  // Ground plane is 60×60; repeating the texture 24× gives blades at
  // roughly real-world scale instead of one absurdly stretched copy.
  tex.repeat.set(24, 24);
  tex.anisotropy = 16;
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

function blendHex(a: number, b: number, t: number): string {
  const ar = (a >> 16) & 0xff,
    ag = (a >> 8) & 0xff,
    ab = a & 0xff;
  const br = (b >> 16) & 0xff,
    bg = (b >> 8) & 0xff,
    bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `rgb(${r},${g},${bl})`;
}

// Procedural wood-grain texture so we don't ship a 256kb image. Returned
// texture has roughly the right look at the board's UV scale.
// Stamp the fabric finish onto a bag material: normal-mapped weave,
// roughness clamped to a "cloth" range, no metalness. Called from every
// bag-material creation site (finger bag, flying bag, pile bag) so the
// fabric look is consistent across modes.
//
// Reuses one cached normal map so all bags share GPU memory.
function applyFabricFinish(mat: MeshStandardMaterial): void {
  mat.normalMap = getFabricNormalMap();
  // Small normal scale — the weave should be subtle, not "scrunched
  // crinkle paper." 0.45 is enough to catch directional light without
  // turning the bag into a textured monster.
  mat.normalScale = new Vector2(0.45, 0.45);
  // Bags are matte fabric — non-metallic, fairly rough. Existing
  // roughness from the asset config is preserved as the baseline;
  // we clamp it into a cloth-appropriate range so a misconfigured
  // 0.0-roughness shiny bag still looks like fabric.
  mat.roughness = Math.max(0.7, mat.roughness);
  mat.metalness = 0;
  mat.needsUpdate = true;
}

// Procedural fabric NORMAL map. Encodes the cross-hatched weave of a
// stuffed cornhole bag as per-pixel surface normals so light hits the
// fabric at varying micro-angles — gives the bag visible "cloth"
// texture without changing its albedo color.
//
// Math: R/G channels carry x/y normal deviation, B is the "up" axis.
// We push small periodic deviations along x (warp) and y (weft), plus
// scattered noise to break the perfectly-regular pattern. Result is
// loaded as a normalMap on every bag material; one shared texture for
// all bags (cached at module scope below).
function makeFabricNormalMap(): CanvasTexture {
  const size = 256;
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d")!;
  const img = ctx.createImageData(size, size);
  // Spacing of the weave: tighter = denser weave. Eight pixels per
  // thread reads as a coarse canvas (cornhole bag duck cloth).
  const warpFreq = (Math.PI * 2) / 8;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Weave: sinusoid along x for warp, along y for weft. Combined,
      // they fight a tiny bit and give the cross-hatched look. Scale
      // is intentionally low — bag fabric is woven tight, not lumpy.
      const warpDx = Math.sin(x * warpFreq) * 0.08;
      const weftDy = Math.sin(y * warpFreq) * 0.08;
      // Pinch of noise so each "thread" has subtle variation. Small
      // amplitude so it doesn't dominate the regular weave.
      const noise = (hashNoise(x, y) - 0.5) * 0.06;
      const nx = warpDx + noise;
      const ny = weftDy + noise;
      const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
      const idx = (y * size + x) * 4;
      img.data[idx + 0] = Math.round((nx * 0.5 + 0.5) * 255);
      img.data[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      img.data[idx + 2] = Math.round(nz * 255);
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new CanvasTexture(c);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  // Tile the weave a few times across the bag's small face so the
  // pattern reads as a continuous fabric grain, not a single mural.
  tex.repeat.set(6, 6);
  return tex;
}

// Procedural wood-grain NORMAL map. Long vertical streaks (same axis
// as the albedo grain in makeWoodTexture) plus subtle cross-grain
// noise. Used on board slabs so the wood looks raised/recessed under
// directional light, not perfectly flat.
function makeWoodGrainNormalMap(): CanvasTexture {
  const w = 256;
  const h = 512;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  const img = ctx.createImageData(w, h);
  // Long, narrow grain bands. Each band has its own pseudo-random
  // offset/phase so the grain reads as natural variation, not a
  // perfect sinusoid.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // Vertical grain: low-freq sinusoid along x, mild perturbation
      // along y to give each "strip" a wandering edge.
      const grainPhase = Math.sin(x * 0.4 + y * 0.005) * 0.12;
      const microNoise = (hashNoise(x, y) - 0.5) * 0.04;
      const knotPhase = Math.sin((x + y * 0.3) * 0.025) * 0.03;
      const nx = grainPhase + microNoise + knotPhase;
      const ny = microNoise * 0.5;
      const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
      const idx = (y * w + x) * 4;
      img.data[idx + 0] = Math.round((nx * 0.5 + 0.5) * 255);
      img.data[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      img.data[idx + 2] = Math.round(nz * 255);
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new CanvasTexture(c);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  return tex;
}

// Deterministic hash-noise — small, fast, no allocation. Hashes (x, y)
// into [0, 1). Used by the procedural normal maps for subtle randomness
// without bringing in a Perlin/simplex library.
function hashNoise(x: number, y: number): number {
  const s = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

// Cached normal maps — generated once on first call, shared across
// every bag and board. Cheap memory; no need to regenerate each time
// a new game starts.
let cachedFabricNormalMap: CanvasTexture | null = null;
function getFabricNormalMap(): CanvasTexture {
  if (!cachedFabricNormalMap) cachedFabricNormalMap = makeFabricNormalMap();
  return cachedFabricNormalMap;
}
let cachedWoodGrainNormalMap: CanvasTexture | null = null;
function getWoodGrainNormalMap(): CanvasTexture {
  if (!cachedWoodGrainNormalMap)
    cachedWoodGrainNormalMap = makeWoodGrainNormalMap();
  return cachedWoodGrainNormalMap;
}

function makeWoodTexture(baseHex: number): CanvasTexture {
  const w = 512;
  const h = 1024; // tall so the grain runs along the board length
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  const base = "#" + baseHex.toString(16).padStart(6, "0");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, w, h);
  // Long vertical grain streaks at varying widths + opacities.
  for (let i = 0; i < 160; i++) {
    const x = Math.random() * w;
    const width = 0.5 + Math.random() * 4;
    const dark = Math.random() < 0.55;
    ctx.fillStyle = dark
      ? `rgba(0,0,0,${0.04 + Math.random() * 0.08})`
      : `rgba(255,240,200,${0.03 + Math.random() * 0.06})`;
    ctx.fillRect(x, 0, width, h);
  }
  // A handful of knots for character.
  for (let i = 0; i < 8; i++) {
    const x = Math.random() * w;
    const y = Math.random() * h;
    const r = 4 + Math.random() * 9;
    ctx.fillStyle = "rgba(30,15,5,0.55)";
    ctx.beginPath();
    ctx.ellipse(x, y, r * 1.8, r * 0.7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.beginPath();
    ctx.ellipse(x, y, r * 0.6, r * 0.25, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  const tex = new CanvasTexture(c);
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.anisotropy = 8;
  return tex;
}

// Builds the slab geometry once. Extrude a rectangular Shape with a
// circular hole Path, bevel-enabled so the cut edges read as finished
// plywood rather than a flat poster. Returns geometry oriented so its
// top face is at +Y; the slab pivot will rake it backwards.
function makeSlabGeometry(dims: BoardDims): ExtrudeGeometry {
  const shape = new Shape();
  const halfW = dims.width / 2;
  const halfL = dims.length / 2;
  shape.moveTo(-halfW, -halfL);
  shape.lineTo(halfW, -halfL);
  shape.lineTo(halfW, halfL);
  shape.lineTo(-halfW, halfL);
  shape.lineTo(-halfW, -halfL);

  // Hole in the back third. The shape lives in the XY plane and we
  // later rotateX(-π/2) the geometry, which maps shape +Y → world -Z.
  // We want the hole to end up at slab local +Z (the side the pivot's
  // rake lifts), so place it at shape -Y here.
  const holeZ = -(halfL - dims.holeFromBack);
  const holePath = new Path();
  holePath.absarc(0, holeZ, dims.holeRadius, 0, Math.PI * 2, false);
  shape.holes.push(holePath);

  const geom = new ExtrudeGeometry(shape, {
    depth: dims.thickness,
    bevelEnabled: true,
    bevelSegments: 3,
    bevelSize: 0.006,
    bevelThickness: 0.004,
    curveSegments: 48,
  });
  geom.rotateX(-Math.PI / 2);
  remapSlabUVs(geom, dims);
  geom.computeVertexNormals();
  return geom;
}

// Rewrites UVs on the top face so the wood-grain texture stretches
// across the full board length without seams from the bevel triangles.
function remapSlabUVs(geom: ExtrudeGeometry, dims: BoardDims): void {
  const pos = geom.attributes.position;
  const uv = geom.attributes.uv;
  if (!pos || !uv) return;
  const halfW = dims.width / 2;
  const halfL = dims.length / 2;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    if (y > dims.thickness * 0.45) {
      uv.setXY(i, (x + halfW) / dims.width, (z + halfL) / dims.length);
    }
  }
  uv.needsUpdate = true;
}

export function createBoard(boardAsset: BoardAsset, dims: BoardDims): BoardHandle {
  const g = new Group();

  // Slab pivot at front-center, ground-level. The slab rotates around
  // the front edge so the back lifts up to rakeRise while the front
  // stays at frontLift above the grass.
  const slabPivot = new Group();
  slabPivot.position.set(0, dims.frontLift, -dims.length / 2);
  slabPivot.rotation.x = -dims.rake;
  g.add(slabPivot);

  const slabGeom = makeSlabGeometry(dims);
  let woodTex = makeWoodTexture(boardAsset.wood);
  const slabMat = new MeshStandardMaterial({
    map: woodTex,
    color: 0xffffff,
    roughness: 0.55,
    metalness: 0.05,
    // Wood-grain micro-normal so directional light catches the
    // raised/recessed grain instead of treating the slab as a flat
    // painted plank. Same texture shared across boards (cheap).
    normalMap: getWoodGrainNormalMap(),
    normalScale: new Vector2(0.5, 0.5),
  });
  // If the board asset specifies a top-deck image, swap it in once
  // the texture finishes loading. Until then we show procedural wood
  // so first paint is never blocked by a network fetch.
  if (boardAsset.topImage) {
    loadImageTexture(boardAsset.topImage).then((tex) => {
      if (!tex) return;
      slabMat.map = tex;
      slabMat.color.set(0xffffff);
      slabMat.needsUpdate = true;
    });
  }
  const slab = new Mesh(slabGeom, slabMat);
  // Slab origin is at the front-center, bottom face at y=0, extending
  // into +Z (back at z=+length).
  slab.position.set(0, 0, dims.length / 2);
  slab.castShadow = true;
  slab.receiveShadow = true;
  slabPivot.add(slab);

  const holeLocalZ = dims.length / 2 - dims.holeFromBack;

  const rim = new Mesh(
    new RingGeometry(dims.holeRadius, dims.holeRadius + 0.012, 48),
    new MeshStandardMaterial({
      color: 0x1a0c04,
      roughness: 0.85,
      transparent: true,
      opacity: 0.7,
    }),
  );
  rim.rotation.x = -Math.PI / 2;
  rim.position.set(0, dims.thickness + 0.001, holeLocalZ);
  slab.add(rim);

  const shadow = new Mesh(
    new RingGeometry(dims.holeRadius + 0.012, dims.holeRadius + 0.045, 48),
    new MeshStandardMaterial({
      color: 0x000000,
      roughness: 1,
      transparent: true,
      opacity: 0.18,
    }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.set(0, dims.thickness + 0.0005, holeLocalZ);
  slab.add(shadow);

  // Two painted stripes for style. Sit a fraction above the top face.
  const stripeMat = new MeshStandardMaterial({
    color: boardAsset.stripe,
    roughness: 0.45,
    metalness: 0.05,
  });
  const stripeFront = new Mesh(
    new PlaneGeometry(dims.width * 0.94, dims.width * 0.045),
    stripeMat,
  );
  stripeFront.rotation.x = -Math.PI / 2;
  stripeFront.position.set(0, dims.thickness + 0.0008, -dims.length * 0.34);
  slab.add(stripeFront);
  const stripeMid = new Mesh(
    new PlaneGeometry(dims.width * 0.94, dims.width * 0.022),
    stripeMat,
  );
  stripeMid.rotation.x = -Math.PI / 2;
  stripeMid.position.set(0, dims.thickness + 0.0008, -dims.length * 0.1);
  slab.add(stripeMid);

  // Back legs. Stay vertical regardless of rake.
  const legHeight = dims.rakeRise + dims.frontLift - dims.thickness / 2;
  const legGeom = new BoxGeometry(0.06, legHeight, 0.06);
  const legMat = new MeshStandardMaterial({
    color: boardAsset.legWood,
    roughness: 0.85,
  });
  for (const x of [-dims.width / 2 + 0.08, dims.width / 2 - 0.08]) {
    const leg = new Mesh(legGeom, legMat);
    leg.position.set(x, legHeight / 2, dims.length / 2 - 0.08);
    leg.castShadow = true;
    g.add(leg);
  }

  return {
    group: g,
    topYAtLocal(localZ) {
      const zFromFront = localZ + dims.length / 2;
      return dims.frontLift + dims.thickness + Math.sin(dims.rake) * zFromFront;
    },
    outcomeAt(resting) {
      const center = g.getWorldPosition(new Vector3());
      const rot = g.rotation.y;
      const dx = resting.x - center.x;
      const dz = resting.z - center.z;
      const cos = Math.cos(-rot);
      const sin = Math.sin(-rot);
      const localX = cos * dx + sin * dz;
      const localZ = -sin * dx + cos * dz;
      const inHole =
        localX * localX + (localZ - holeLocalZ) ** 2 <
        dims.holeRadius * dims.holeRadius;
      if (inHole) return "in_hole";
      const onBoard =
        Math.abs(localX) <= dims.width / 2 &&
        Math.abs(localZ) <= dims.length / 2;
      return onBoard ? "on_board" : "miss";
    },
    // repaint accepts the new scene palette + the current board asset so
    // we can refresh both the procedural wood texture (tinted by the
    // board's wood color) and the stripe/leg colors without rebuilding
    // the geometry.
    repaint(_palette, board) {
      const wood = board?.wood ?? boardAsset.wood;
      const stripe = board?.stripe ?? boardAsset.stripe;
      const leg = board?.legWood ?? boardAsset.legWood;
      const topImage = board?.topImage ?? boardAsset.topImage;
      woodTex.dispose();
      woodTex = makeWoodTexture(wood);
      // Procedural wood paints immediately. If the new board carries a
      // topImage we kick off the async load; once it resolves it
      // overrides the wood texture. Order matters: assign procedural
      // first so the live swap is seamless even when the user picks
      // back to an image-less preset.
      slabMat.map = woodTex;
      slabMat.color.set(0xffffff);
      slabMat.needsUpdate = true;
      if (topImage) {
        loadImageTexture(topImage).then((tex) => {
          if (!tex) return;
          slabMat.map = tex;
          slabMat.needsUpdate = true;
        });
      }
      stripeMat.color.set(stripe);
      legMat.color.set(leg);
    },
  };
}

type BagBody = {
  mesh: Mesh;
  step: (dt: number) => void;
  simulate: (board: BoardHandle) => Promise<ThrowResult>;
  // Slab-local (x, z) of the bag if it's currently sitting (or sliding)
  // on its target slab. null when the bag is in the air, in the hole,
  // or resting on the grass.
  getSlabLocal: () => { x: number; z: number } | null;
  // Re-energize a slab-rested bag with the given slab-local velocity
  // delta. Used by collision response when a sliding bag hits this one.
  kickSlide: (dvx: number, dvz: number) => void;
};

// Rounded-corner extruded bag geometry. Replaces the old BoxGeometry —
// a real corn-filled bag has soft corners + a slight pillow bevel, so a
// straight box read as a blocky brick at any zoom. Material indices on
// an ExtrudeGeometry are: 0 = front+back (top+bottom after the rotate),
// 1 = side walls. So an [topMaterial, sideMaterial] array on the mesh
// lets us paint a photo / illustration onto the top + bottom faces while
// keeping the cloth side walls a solid color.
// Rewrites cap-face UVs (top + bottom of the bag) so the texture
// covers the whole face. ExtrudeGeometry's default UVs use the raw
// shape coordinates (e.g. -0.15..0.15 for a 0.3m bag), so a single
// sliver of the texture gets sampled across the entire face — the
// rest reads as edge-clamp grey. Side walls are left untouched
// (their UVs are perimeter-based and read correctly as-is).
// Apply the bag's PER-ASSET texture transform to a freshly-loaded
// texture. This is the single source of truth — every place that
// wraps a bag image (player finger bag, pile bags, in-flight bag,
// rig-held bag) calls this so the same image lines up consistently
// across all of them. Values come from the BagAsset itself (set in
// /admin/design's alignment tool); missing fields fall back to the
// identity transform (no shift, no zoom, no rotation).
function applyBagTextureTransform(tex: Texture, bag: BagAsset): void {
  const s = Math.max(0.05, bag.texScale ?? 1);
  const u = bag.texOffsetU ?? 0;
  const v = bag.texOffsetV ?? 0;
  const rot = ((bag.texRotationDeg ?? 0) * Math.PI) / 180;
  // Negative repeat flips the axis. With three.js's UV matrix
  // centered at (0.5, 0.5), offset is just the pan amount — no
  // 0.5 - 0.5/s compensation needed. (Older formula did that and
  // accidentally double-shifted the crop, which only worked at
  // scale=1 because the bonus term zeroed out; with flip on it
  // pushed the sampled UV past 1 and the bag rendered as a solid
  // edge-color stripe — that's why textures vanished after
  // enabling flip.)
  const fu = bag.texFlipU ? -1 : 1;
  const fv = bag.texFlipV ? -1 : 1;
  tex.center.set(0.5, 0.5);
  tex.rotation = rot;
  tex.repeat.set(fu / s, fv / s);
  tex.offset.set(u, v);
  tex.needsUpdate = true;
}

// Bag-change detection that catches both ID swaps AND in-place
// metadata edits (alignment tool saved new offsets/scale/rotation
// on the SAME bag id). setLoadout uses this so the renderer rebuilds
// the finger bag + pile when alignment changes — without it the
// admin's Save lands in the DB but the player keeps seeing the old
// texture transform until full page reload.
function bagChanged(
  prev: BagAsset | undefined,
  next: BagAsset,
): boolean {
  if (!prev) return true;
  if (prev.id !== next.id) return true;
  if (prev.topImage !== next.topImage) return true;
  if ((prev.texOffsetU ?? 0) !== (next.texOffsetU ?? 0)) return true;
  if ((prev.texOffsetV ?? 0) !== (next.texOffsetV ?? 0)) return true;
  if ((prev.texScale ?? 1) !== (next.texScale ?? 1)) return true;
  if ((prev.texRotationDeg ?? 0) !== (next.texRotationDeg ?? 0)) return true;
  if ((prev.texFlipU ?? false) !== (next.texFlipU ?? false)) return true;
  if ((prev.texFlipV ?? false) !== (next.texFlipV ?? false)) return true;
  return false;
}

function remapBagCapUVs(
  geom: ExtrudeGeometry,
  _size: number,
  _thickness: number,
): void {
  const pos = geom.attributes.position;
  const uv = geom.attributes.uv;
  if (!pos || !uv) return;
  // Project EVERY vertex (cap + bevel + side walls) onto a top-down
  // UV plane using the geometry's actual bounding box. The previous
  // implementation only remapped vertices whose |y| exceeded a
  // threshold (the flat cap face) and used the original shape size
  // for normalization — that left two visible mismatches on
  // textured bags:
  //   1. The bevel + side walls (outside the threshold) kept their
  //      perimeter-based UVs from ExtrudeGeometry, so they showed
  //      the default bag color instead of the photo. From a 45°
  //      tilt this reads as a "grey frame" ~20% wider than the
  //      textured cap.
  //   2. The cap itself is INSET from the side wall by bevelSize,
  //      so size-based normalization mapped cap UVs to 0.03..0.97
  //      instead of 0..1 — the texture didn't even fill the cap
  //      it lived on.
  // Using the actual bounding box guarantees the texture stretches
  // from the bag's leftmost vertex (UV=0) to its rightmost (UV=1)
  // along both x and z. Sides + bevel sample the texture's edge
  // regions, which for a photographed bag is the bag's outer
  // fabric — natural-looking wrap with no margin strip.
  geom.computeBoundingBox();
  const bbox = geom.boundingBox!;
  const xRange = Math.max(1e-6, bbox.max.x - bbox.min.x);
  const zRange = Math.max(1e-6, bbox.max.z - bbox.min.z);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    uv.setXY(i, (x - bbox.min.x) / xRange, (z - bbox.min.z) / zRange);
  }
  uv.needsUpdate = true;
}

function makeBagGeometry(size: number, thickness: number): ExtrudeGeometry {
  const half = size / 2;
  // Sharp corner radius — 7.2% of edge length (20% tighter than the
  // previous 9%). Corners look stitched and crisp; pillow read
  // comes from the cap bulge + the side pinch below, not from
  // generous rounding. The radius isn't zero because hard points
  // would cause visible mesh faceting where the side curves meet
  // the corner curve.
  const r = Math.min(size * 0.072, 0.05);
  // Sides bow INWARD (concave) by 3.5% of edge length at the
  // midpoint — a hair deeper than before so the indent reads at
  // game distance, but still well short of the dramatic 5% pinch
  // that felt over-sculpted. Quadratic Bezier midpoint sits at
  // half the control offset, so ctrl = 2 × sideSuck.
  const sideSuck = size * 0.035;
  const ctrl = sideSuck * 2;
  const shape = new Shape();
  shape.moveTo(-half + r, -half);
  // Bottom edge: curve INWARD (toward +Y/center)
  shape.quadraticCurveTo(0, -half + ctrl, half - r, -half);
  // Bottom-right corner: curve OUTWARD (toward +X/+Y from corner point)
  shape.quadraticCurveTo(half, -half, half, -half + r);
  // Right edge: curve INWARD (toward -X/center)
  shape.quadraticCurveTo(half - ctrl, 0, half, half - r);
  // Top-right corner
  shape.quadraticCurveTo(half, half, half - r, half);
  // Top edge: curve INWARD (toward -Y/center)
  shape.quadraticCurveTo(0, half - ctrl, -half + r, half);
  // Top-left corner
  shape.quadraticCurveTo(-half, half, -half, half - r);
  // Left edge: curve INWARD (toward +X/center)
  shape.quadraticCurveTo(-half + ctrl, 0, -half, -half + r);
  // Bottom-left corner
  shape.quadraticCurveTo(-half, -half, -half + r, -half);
  // Bigger bevel + more bevel segments give a softer, more rounded
  // perimeter that reads as fabric rolled over a stuffed core. The
  // bevel is the visible "lip" between the flat cap and the side wall;
  // pushing bevelSize up makes the bag look more inflated.
  const bevel = thickness * 0.32;
  const geom = new ExtrudeGeometry(shape, {
    depth: thickness - bevel * 2,
    bevelEnabled: true,
    // Bigger numbers → smoother round-over. Cheap: 8 bevel segments
    // and 18 curve segments add ~150 verts per bag, immaterial cost.
    bevelSegments: 8,
    bevelSize: bevel * 1.5,
    bevelThickness: bevel,
    curveSegments: 18,
  });
  // ExtrudeGeometry sits in XY with extrusion along +Z. Rotate so the
  // extrusion axis becomes +Y (vertical) — top face faces up.
  geom.rotateX(-Math.PI / 2);
  // Center the geometry vertically so the bag pivot stays at its
  // centroid (the physics expects bag origin at the cube center).
  geom.translate(0, -(thickness - bevel * 2) / 2, 0);
  // NOTE: the pillow Y-deformation (dome the caps + pinch the
  // perimeter) was REMOVED. Mapping a flat bag photo onto that domed,
  // segmented, pinched surface stretched the texture unevenly and the
  // recomputed normals lit the segment rings as faint ridges — reading
  // as unnatural "crease / frame-joint" lines through the artwork. The
  // bag now keeps a FLAT cap so the photo maps 1:1 and cleanly; the
  // soft silhouette comes from the rounded corners, the gently concave
  // sides, and the bevel above — no surface deformation needed.
  // Cap UVs default to raw shape coordinates; normalize them so a
  // bag photo fills the full top/bottom face instead of being clipped
  // to a 15% sliver.
  remapBagCapUVs(geom, size, thickness);
  geom.computeVertexNormals();
  return geom;
}

// Optional config to drive the bag on a deterministic parabolic arc
// instead of running physics. Used for ghost throws so ghost-N reliably
// scores exactly N points per round, with no chance of physics drift
// (edge clips, slide-into-hole, mid-air collisions) deviating from the
// scripted plan. The flight LOOKS like a real throw (parabola) but the
// landing is forced.
type ScriptedFlight = {
  target: Vector3; // world-space resting position
  outcome: "in_hole" | "on_board" | "miss";
  flightTime: number; // seconds in the air before "landing"
  // Pre-throw HOLD: seconds the bag sits visible at the launch
  // position before the arc begins. Gives the player a beat to see
  // the opponent's bag in their hand-equivalent spot ("they're about
  // to throw") rather than the bag appearing mid-flight. 0 = no
  // hold (skip the pause, fly immediately).
  holdSeconds: number;
};

// Deterministic resting yaw from a world landing position. A bag's
// final orientation must be IDENTICAL on every device — but the
// in-flight yaw spin accumulates over RAF frames, and RAF cadence
// differs per device (and a remote viewer replays the throw as a
// scripted arc, not the originator's physics), so the spun-up yaw
// would never match. Instead we DISCARD the accumulated yaw at rest
// and snap to a value hashed from the landing x/z. Since the landing
// is shared exactly across devices (stored in the throw event), every
// device computes the same resting yaw. Classic sin-hash → [0, 2π).
function seededYaw(x: number, z: number): number {
  const s = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
  return (s - Math.floor(s)) * Math.PI * 2;
}

function createBag(
  start: Vector3,
  velocity: Vector3,
  bagAsset: BagAsset,
  dims: BoardDims,
  // Reference to the bag pool so this bag's slide can collide with
  // others resting on the same slab. The closure dereferences the
  // array on each step so newly-spawned bags get included.
  bagPool: BagBody[] = [],
  scripted: ScriptedFlight | null = null,
  // Fired once, at the moment this bag drops into the hole (real
  // physics OR scripted in_hole drop). The scene layer uses it to
  // flash a "+3" by the hole. Optional — color-only / preview bags
  // pass nothing.
  onInHole?: () => void,
): BagBody {
  // Imaged bags: ONE material wrapping every face. The geometry's
  // UV map (remapBagCapUVs) projects every vertex — caps, bevels,
  // and sides — onto a top-down UV plane. The side + bevel verts
  // therefore sample the texture's edge pixels, which in a properly
  // generated bag image hold the bag's body fabric color. That's
  // what makes the silhouette read as a continuous fabric pouch
  // instead of a textured top sitting on a wood block.
  //
  // For COLOR-ONLY bags (no image, which doesn't apply to the
  // production catalog after we removed the seed bags) we keep the
  // [topMat, sideMat] split so the sides can be a touch rougher
  // than the broad faces.
  let mesh: Mesh;
  if (bagAsset.topImage) {
    // Note: many imaged bags have color=0xFFFFFF as their metadata
    // baseline (the IMAGE supplies the actual color). Don't use that
    // raw color anywhere — let the texture be the only color source.
    const mat = new MeshStandardMaterial({
      color: 0xffffff,
      roughness: bagAsset.roughness,
      metalness: 0,
    });
    applyFabricFinish(mat);
    loadImageTexture(bagAsset.topImage).then((tex) => {
      if (!tex) return;
      applyBagTextureTransform(tex, bagAsset);
      mat.map = tex;
      mat.needsUpdate = true;
    });
    const tCb = getTuning();
    mesh = new Mesh(makeBagGeometry(tCb.bagSize, tCb.bagThickness), mat);
  } else {
    const topMat = new MeshStandardMaterial({
      color: bagAsset.color,
      roughness: bagAsset.roughness,
      metalness: 0,
    });
    const sideMat = new MeshStandardMaterial({
      color: bagAsset.color,
      roughness: Math.min(1, bagAsset.roughness + 0.03),
      metalness: 0,
    });
    applyFabricFinish(topMat);
    applyFabricFinish(sideMat);
    const tCb = getTuning();
    mesh = new Mesh(
      makeBagGeometry(tCb.bagSize, tCb.bagThickness),
      [topMat, sideMat],
    );
  }
  mesh.position.copy(start);
  // Visual scale for EVERY created bag (player-thrown + scripted
  // opponent/ghost). This is the SINGLE bag-size knob — fingerBagScale
  // — shared with the in-hand finger bag (applyFingerBagTransform) and
  // the pile bags (rebuildPile). Previously this read bagWorldScale, a
  // SEPARATE knob: the in-flight squash integrator (applySquashToMesh)
  // multiplies baseScale every frame and overwrote the post-create
  // `mesh.scale.copy(fingerBag.scale)` that tried to align them — so a
  // thrown/opponent bag silently rendered at bagWorldScale while the
  // in-hand bag used fingerBagScale. If an admin dialed the two apart
  // (they did), the opponent bag looked a different size + sat higher.
  // Reading fingerBagScale here makes all bags identical, full stop.
  const baseScale = getTuning().fingerBagScale;
  mesh.scale.setScalar(baseScale);
  mesh.castShadow = true;
  // Receive shadows too — bags piled on each other (a stack of 3-4
  // settled on the board) cast onto the bags below. Without this,
  // a pile reads as a flat overlay; with it, depth is unmistakable.
  // Cost: one extra shadow-map lookup per bag fragment, negligible.
  mesh.receiveShadow = true;
  // Random initial yaw so every throw starts pointing somewhere
  // different — a real bag never leaves your hand perfectly aligned.
  mesh.rotation.y = Math.random() * Math.PI * 2;
  const v = velocity.clone();
  // Per-bag jitter factor (±30%) baked in once at spawn so each bag
  // has its own personality. The base spin rate is read from
  // getTuning() each frame inside step/slideStep, so live knob
  // changes are reflected immediately on all in-flight bags.
  const spinJitter = 0.7 + Math.random() * 0.6;
  const currentAngularY = () => getTuning().bagYawSpinRate * spinJitter;
  let restedBoard: BoardHandle | null = null;
  let resolveSim: ((r: ThrowResult) => void) | null = null;

  // Phases:
  //   "fly"   — free-fall + air spin
  //   "slide" — constrained to slab top, friction + slope decel
  //   "drop"  — fell through hole, vertical free-fall to ground inside
  //             the hole; settles below board for outcome computation
  //   "rest"  — settled. No-op per-frame, but a rested bag on the slab
  //             can still be kicked back into "slide" by a collision.
  type Phase = "fly" | "slide" | "drop" | "rest";
  let phase: Phase = "fly";
  // True while the bag is in physical contact with its target slab —
  // sliding OR resting on it. False when in the air, in the hole, or
  // sitting on the lawn.
  let onSlab = false;

  // Slab-local slide state. Valid whenever onSlab is true.
  let slideX = 0;
  let slideZ = 0;
  let slideVx = 0;
  let slideVz = 0;

  function edgeTolerance(): number {
    const t = getTuning();
    return t.bagSize * t.bagEdgeTolerance;
  }

  // Elapsed time tracker for scripted flight. Only used when `scripted`
  // is non-null. Counts up each step; the bag arcs to a touchdown point
  // SHORT of its target, then slides the rest of the way so the landing
  // reads like a real throw (slide + settling spin) instead of a static
  // thump — yet still ends byte-identically on every device because the
  // touchdown, slide path, and final pose are all derived from the
  // shared landing.
  let scriptedElapsed = 0;
  // Lazily-computed touchdown (where the arc meets the surface, short of
  // the target) + the yaw at slide-start (for easing to the final
  // deterministic yaw). Computed once on the first airborne frame.
  let scriptedTouchdown: Vector3 | null = null;
  let scriptedSlideYaw0 = 0;
  let scriptedSlideBegun = false;
  let scriptedDropBegun = false;
  // Seconds the bag spends sliding from touchdown to the target.
  const SCRIPTED_SLIDE_SECONDS = 0.5;
  // For scripted in_hole: after the slide lands the bag on the deck
  // above the hole, it visibly DROPS into the throat over this long,
  // sinking this far below the rim. Without a real drop the replayed
  // 3-pointer just stuck at the surface (or, for player-originated
  // landings stored at the hole BOTTOM, arced under the deck and
  // vanished). The drop is what reads as "it went in."
  const SCRIPTED_DROP_SECONDS = 0.45;
  const SCRIPTED_DROP_DEPTH = 0.42;

  // ---- Procedural soft-body fake.
  //
  // Real cornhole bags are fabric pouches stuffed with kernels — they
  // squash flat on landing, retain a slightly compressed silhouette at
  // rest, and visibly cushion when hit by another bag. We don't run a
  // soft-body solver (too expensive on mobile); instead we drive a
  // per-axis scale multiplier that animates over ~250ms after each
  // impact and settles into a slightly-flattened rest shape.
  //
  // Mass conservation cheat: compressing Y while expanding XZ makes the
  // bag look like its volume is preserved (like a real fabric envelope).
  // Same numbers used by every bag, deterministic — both phones in a
  // multiplayer game render identical squash from identical impacts.
  //
  // Shape semantics:
  //   squashY      = current Y multiplier (1.0 = full thickness, ~0.6
  //                  on hard impact, ~0.88 settled on slab)
  //   squashXZ     = current XZ multiplier (1.0 = full footprint, ~1.15
  //                  on hard impact, ~1.04 settled)
  //   squashYTarget / squashXZTarget = where we're animating toward
  //   squashLerp   = per-second blend rate; ~12 = ~80ms to mostly close
  //                  the gap, matches how a stuffed bag would settle
  let squashY = 1.0;
  let squashXZ = 1.0;
  let squashYTarget = 1.0;
  let squashXZTarget = 1.0;
  // Lower lerp rate = slower spring-back = more visible squash. At 5
  // per second, the bag spends ~200ms visibly compressed before easing
  // toward its settled silhouette. Anything faster blew past the eye
  // on mobile displays.
  const SQUASH_LERP = 5;
  // Triggers a squash given an impact intensity in [0..1]. 0 = no impact
  // (smooth landing), 1 = full thump. Called once at the moment of
  // contact; the per-frame integrator does the rest.
  function applySquashImpact(intensity: number): void {
    const k = Math.max(0, Math.min(1, intensity));
    // DRAMATIC compression at the moment of impact. A real fabric bag
    // stuffed with kernels flattens to 40-50% of its loose thickness
    // when it slaps down on wood. Reads as "this thing has weight."
    squashY = 1 - 0.55 * k; // 1.0 → 0.45 at max impact
    squashXZ = 1 + 0.3 * k; // 1.0 → 1.30 at max impact
    // Final settled shape: visibly flatter and wider than the
    // geometric "unloaded" pose. This is what the bag looks like
    // sitting on the board AFTER the impact animation finishes.
    squashYTarget = 1 - 0.22; // settled at 0.78 Y
    squashXZTarget = 1 + 0.1; // settled at 1.10 XZ
  }
  // Reset to flying-bag pose: no compression, lerping fast back to 1.0.
  // Used when a settled bag is kicked back into slide by a collision.
  function resetSquashForFlight(): void {
    squashYTarget = 1.0;
    squashXZTarget = 1.0;
  }
  // Apply the current squash to the mesh. Called every frame regardless
  // of phase so the settling animation continues during "rest".
  function applySquashToMesh(): void {
    mesh.scale.set(
      baseScale * squashXZ,
      baseScale * squashY,
      baseScale * squashXZ,
    );
  }
  // Per-frame lerp toward target. Cheap: three multiplies + two lerps
  // per bag per frame.
  function stepSquash(dt: number): void {
    const a = 1 - Math.exp(-SQUASH_LERP * dt);
    squashY += (squashYTarget - squashY) * a;
    squashXZ += (squashXZTarget - squashXZ) * a;
    applySquashToMesh();
  }

  function step(dt: number) {
    // Squash animation continues even in "rest" so the just-landed
    // compression eases back toward the settled silhouette. Cheap and
    // it ALSO covers the case where a collision revives a rested bag
    // (we want its squash to relax during the kick-into-slide phase
    // and re-trigger if it gets thumped again).
    stepSquash(dt);
    if (phase === "rest") return; // settled — collisions can revive it
    const tune = getTuning();

    // Scripted flight: drive position along a precomputed parabolic
    // arc and force the outcome on landing. Skips ALL physics
    // (free-fall, slide, collisions) because the user explicitly
    // wants ghost-N to always score N points — no exceptions.
    if (scripted && (phase === "fly" || phase === "drop")) {
      scriptedStep(dt, tune);
      return;
    }

    if (phase === "slide") {
      slideStep(dt, tune);
      return;
    }

    // Free-fall (phase === "fly" or "drop").
    v.y += tune.gravity * dt;
    mesh.position.addScaledVector(v, dt);
    mesh.rotation.y += currentAngularY() * dt;
    mesh.rotation.x += v.length() * dt * 0.3;

    // Board landing check (only when flying — once we're past it, we're
    // either sliding or have dropped into the hole).
    if (phase === "fly" && restedBoard && v.y < 0) {
      const localFrame = boardLocal(restedBoard, mesh.position);
      const tol = edgeTolerance();
      const onSlabXZ =
        Math.abs(localFrame.x) <= dims.width / 2 - tol &&
        Math.abs(localFrame.z) <= dims.length / 2 - tol;
      if (onSlabXZ) {
        const topY = restedBoard.topYAtLocal(localFrame.z);
        if (mesh.position.y <= topY + getTuning().bagThickness / 2) {
          // Did the bag fall directly into the hole on first contact?
          const overHole =
            localFrame.x * localFrame.x +
              (localFrame.z - (dims.length / 2 - dims.holeFromBack)) ** 2 <
            dims.holeRadius * dims.holeRadius;
          if (overHole) {
            beginDrop();
            return;
          }
          beginSlide(localFrame, topY);
          return;
        }
      }
    }

    // Ground.
    if (mesh.position.y <= getTuning().bagThickness / 2) {
      // Intensity from downward velocity: 0 at gentle 0.5 m/s, 1 at
      // hard 6 m/s+. Maps the felt thump to a squash amount.
      const impact = Math.min(1, Math.max(0, (-v.y - 0.5) / 5.5));
      applySquashImpact(impact);
      mesh.position.y = getTuning().bagThickness / 2;
      v.set(v.x * 0.2, 0, v.z * 0.2);
      if (v.length() < 0.05) {
        v.set(0, 0, 0);
        // Only the MISS (lawn) thud here. A bag in the "drop" phase
        // fell THROUGH the hole — it already played the in-hole sound
        // at beginDrop and is just settling to grade inside the hole,
        // so playing the on-ground sound too would double up (the bug:
        // "in the hole" also triggered "on the ground").
        if (phase !== "drop") playBagOnGround();
        finalize();
      }
    }
  }

  function beginSlide(
    localFrame: { x: number; z: number },
    topY: number,
  ) {
    if (!restedBoard) return;
    const tune = getTuning();
    // Squash impact based on incoming Y velocity. The board is a hard
    // wood surface — same impact curve as ground landings.
    const impact = Math.min(1, Math.max(0, (-v.y - 0.5) / 5.5));
    applySquashImpact(impact);
    const rot = restedBoard.group.rotation.y;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    slideVx = cosR * v.x - sinR * v.z;
    slideVz = sinR * v.x + cosR * v.z;
    // Inelastic Y absorb: kill vertical velocity, retain bagInelasticY
    // fraction of horizontal momentum.
    slideVx *= tune.bagInelasticY;
    slideVz *= tune.bagInelasticY;
    slideX = localFrame.x;
    slideZ = localFrame.z;
    v.set(0, 0, 0);
    mesh.position.y = topY + getTuning().bagThickness / 2;
    // Pitch the bag to match the board's rake so it sits FLUSH on
    // the slab instead of intersecting it. dims.rake is the slope
    // angle in radians (back-end-raised); a far-board bag needs
    // rotation.x = -rake to tilt its top face parallel to the slab.
    // Plus the admin's tuning offset for fine-tuning per-board.
    const tuneNow = getTuning();
    const pitchOffsetRad = (tuneNow.bagLandedPitchOffsetDeg * Math.PI) / 180;
    const yRot = restedBoard.group.rotation.y;
    // For near-board (rotated π), the rake direction reverses — use
    // cos(yRot) so far=−rake, near=+rake without a special case.
    mesh.rotation.x = -dims.rake * Math.cos(yRot) + pitchOffsetRad;
    // Clear any accumulated air-tumble around Z (the rotation.x +=
    // v.length() line in step() puts it there) so the bag sits
    // squarely on the slab.
    mesh.rotation.z = 0;
    phase = "slide";
    onSlab = true;
    playBagLand();
    if (Math.hypot(slideVx, slideVz) > 0.4) startBagSlide();
  }

  // Drive the scripted bag along a fixed parabola from start to
  // scripted.target over scripted.flightTime. When the arc completes,
  // snap the bag into the correct landed state (slide/drop/ground)
  // for the forced outcome and resolve simulate. Visually this still
  // looks like a thrown bag — same arc shape, same yaw spin — but
  // the landing point is locked in.
  function scriptedStep(dt: number, tune: ReturnType<typeof getTuning>) {
    if (!scripted) return;
    scriptedElapsed += dt;
    // Pre-throw hold: bag sits at start position before flying. The
    // mesh was already placed at `start` by createBag's
    // mesh.position.copy(start), so doing nothing per-frame keeps it
    // there. This is the "show the right bag for a second, then
    // throw" beat — without it the bag would appear mid-air with no
    // visible buildup.
    if (scriptedElapsed < scripted.holdSeconds) {
      return;
    }
    const flightElapsed = scriptedElapsed - scripted.holdSeconds;
    const tgt = scripted.target;
    const bagHalf = getTuning().bagThickness / 2;

    // ---- Compute the touchdown point once (deterministic). The bag
    // arcs to here — short of the target along the approach line, ON
    // the surface — then slides forward to the target. slideBack is
    // bounded so a near throw doesn't slide backwards past the start.
    if (!scriptedTouchdown) {
      const dx = tgt.x - start.x;
      const dz = tgt.z - start.z;
      const len = Math.hypot(dx, dz) || 1;
      const slideBack = Math.min(0.32, len * 0.35);
      const tx = tgt.x - (dx / len) * slideBack;
      const tz = tgt.z - (dz / len) * slideBack;
      let ty: number;
      if (scripted.outcome === "miss") {
        ty = bagHalf; // lawn
      } else {
        // The slab top is a plane raked along +z (back higher), so its
        // height varies only with z at slope rakeRise/length. target.y
        // is the surface height at target.z; touchdown sits (tgt.z−tz)
        // closer to the front, i.e. that much lower.
        const slope = dims.length > 0 ? dims.rakeRise / dims.length : 0;
        ty = tgt.y - (tgt.z - tz) * slope;
      }
      scriptedTouchdown = new Vector3(tx, ty, tz);
    }
    const td = scriptedTouchdown;

    const T = scripted.flightTime;

    // ---- Phase 1: parabolic arc, start → touchdown.
    if (flightElapsed < T) {
      const u = flightElapsed / T;
      const x = start.x + (td.x - start.x) * u;
      const z = start.z + (td.z - start.z) * u;
      const peak = Math.max(start.y, td.y) + 1.2;
      const y =
        (1 - u) * (1 - u) * start.y +
        2 * (1 - u) * u * peak +
        u * u * td.y;
      mesh.position.set(x, y, z);
      mesh.rotation.y += currentAngularY() * dt;
      mesh.rotation.x += 0.8 * dt;
      return;
    }

    // ---- Phase 2: slide, touchdown → target, with an ease-OUT so it
    // decelerates like friction. Yaw eases to the deterministic resting
    // value; pitch eases to the slab rake. The first slide frame fires
    // the landing thump (squash + sound).
    const slideU = Math.min(
      1,
      (flightElapsed - T) / SCRIPTED_SLIDE_SECONDS,
    );
    const finalYaw = seededYaw(tgt.x, tgt.z);
    const pitchOffsetRad = (tune.bagLandedPitchOffsetDeg * Math.PI) / 180;
    // Far board has no Y rotation, so the rake-matched pitch is just
    // -rake (+ the admin offset). Mirrors the old scripted landing,
    // which read yRot=0 for scripted bags (restedBoard was null).
    const restPitch =
      scripted.outcome === "on_board" ? -dims.rake + pitchOffsetRad : 0;

    if (!scriptedSlideBegun) {
      scriptedSlideBegun = true;
      scriptedSlideYaw0 = mesh.rotation.y;
      // Landing thump at first contact — softer than a player slap;
      // the slide carries the rest of the energy.
      if (scripted.outcome === "miss") {
        playBagOnGround();
        applySquashImpact(0.4);
      } else {
        playBagLand();
        applySquashImpact(0.5);
      }
    }

    if (slideU < 1) {
      const e = 1 - (1 - slideU) * (1 - slideU); // ease-out (decel)
      const x = td.x + (tgt.x - td.x) * e;
      const z = td.z + (tgt.z - td.z) * e;
      // Interpolating Y between two on-surface points stays on the
      // (planar) slab. For miss it's flat lawn anyway.
      const y = td.y + (tgt.y - td.y) * e;
      mesh.position.set(x, y, z);
      // Ease the air spin down to the resting yaw — a natural settling
      // rotation rather than an abrupt snap.
      mesh.rotation.y =
        scriptedSlideYaw0 + (finalYaw - scriptedSlideYaw0) * e;
      mesh.rotation.x = restPitch * e;
      mesh.rotation.z = 0;
      return;
    }

    // ---- Phase 2.5 (in_hole only): the slide has parked the bag on the
    // deck above the hole (tgt.y was normalized to the slab surface in
    // throwBagScripted). Now drop it into the throat — an ease-IN
    // descent like a real fall — so the cornhole is unmistakable. Fire
    // the in-hole sound + "+3" flash at the first drop frame.
    if (scripted.outcome === "in_hole") {
      const dropElapsed = flightElapsed - T - SCRIPTED_SLIDE_SECONDS;
      if (!scriptedDropBegun) {
        scriptedDropBegun = true;
        playBagInHole();
        onInHole?.();
        onSlab = false;
      }
      const dropU = Math.min(1, dropElapsed / SCRIPTED_DROP_SECONDS);
      mesh.rotation.x = 0;
      mesh.rotation.z = 0;
      mesh.rotation.y = finalYaw;
      if (dropU < 1) {
        const e = dropU * dropU; // accelerate downward
        mesh.position.set(tgt.x, tgt.y - SCRIPTED_DROP_DEPTH * e, tgt.z);
        return;
      }
      mesh.position.set(tgt.x, tgt.y - SCRIPTED_DROP_DEPTH, tgt.z);
      phase = "rest";
      resolveSim?.({
        outcome: "miss", // outer wrapper trusts our forced outcome
        resting: {
          x: mesh.position.x,
          y: mesh.position.y,
          z: mesh.position.z,
        },
      });
      return;
    }

    // ---- Phase 3: settle exactly on the shared target + final pose
    // (on_board / miss — these don't drop).
    mesh.position.set(tgt.x, tgt.y, tgt.z);
    mesh.rotation.x = restPitch;
    mesh.rotation.z = 0;
    mesh.rotation.y = finalYaw;
    onSlab = scripted.outcome === "on_board";
    phase = "rest";
    resolveSim?.({
      outcome: "miss", // outer wrapper trusts our forced outcome
      resting: {
        x: mesh.position.x,
        y: mesh.position.y,
        z: mesh.position.z,
      },
    });
  }

  function beginDrop() {
    phase = "drop";
    onSlab = false;
    stopBagSlide();
    playBagInHole();
    onInHole?.();
    // Gentle downward drift; existing free-fall integration handles the rest.
    v.set(v.x * 0.1, -0.8, v.z * 0.1);
  }

  function slideStep(dt: number, tune = getTuning()) {
    if (!restedBoard) return;

    // Slope: world gravity component along slab local +z (= uphill toward
    // the hole/back). gravity is signed; this is negative so the slope
    // pulls the bag toward the low end (-z).
    slideVz += tune.gravity * Math.sin(dims.rake) * dt;

    // Friction opposes the velocity vector.
    const speed = Math.hypot(slideVx, slideVz);
    if (speed > 0.0001) {
      const decel =
        tune.frictionMu * Math.abs(tune.gravity) * Math.cos(dims.rake) * dt;
      if (decel >= speed) {
        slideVx = 0;
        slideVz = 0;
      } else {
        slideVx -= (slideVx / speed) * decel;
        slideVz -= (slideVz / speed) * decel;
      }
    }

    slideX += slideVx * dt;
    slideZ += slideVz * dt;

    // Collide with other bags on the same slab. Equal-mass perfectly-
    // inelastic 1D collision along the connecting line: incoming bag
    // loses half its normal-component velocity, target gains it. So a
    // bag rolling into a stationary one stops or slows; a fast bag can
    // push a parked bag forward.
    const minDist = getTuning().bagSize * 0.85;
    for (const other of bagPool) {
      if (other.mesh === mesh) continue;
      const ol = other.getSlabLocal();
      if (!ol) continue;
      const dx = slideX - ol.x;
      const dz = slideZ - ol.z;
      const dist = Math.hypot(dx, dz);
      if (dist >= minDist || dist < 0.001) continue;
      const nx = dx / dist;
      const nz = dz / dist;
      const vN = slideVx * nx + slideVz * nz; // signed projection on n
      if (vN < 0) {
        const halfTransfer = -vN / 2; // positive
        slideVx += halfTransfer * nx;
        slideVz += halfTransfer * nz;
        other.kickSlide(-halfTransfer * nx, -halfTransfer * nz);
      }
      // Separate so we don't sit on top of the other bag.
      const overlap = minDist - dist;
      slideX += nx * overlap * 0.5;
      slideZ += nz * overlap * 0.5;
    }

    // Re-project to world for rendering.
    const center = restedBoard.group.getWorldPosition(new Vector3());
    const rot = restedBoard.group.rotation.y;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    mesh.position.x = center.x + cosR * slideX + sinR * slideZ;
    mesh.position.z = center.z + (-sinR * slideX + cosR * slideZ);
    mesh.position.y = restedBoard.topYAtLocal(slideZ) + getTuning().bagThickness / 2;
    // Constant CW drift plus a small slide-velocity-driven roll.
    // Rate read live so admins can dial it up/down while bags are
    // mid-slide.
    mesh.rotation.y += currentAngularY() * dt + slideVx * dt * 0.7;

    // Hole check — sliding bag drops in.
    const holeZ = dims.length / 2 - dims.holeFromBack;
    const overHole =
      slideX * slideX + (slideZ - holeZ) ** 2 <
      dims.holeRadius * dims.holeRadius;
    if (overHole) {
      v.x = cosR * slideVx + sinR * slideVz;
      v.z = -sinR * slideVx + cosR * slideVz;
      beginDrop();
      return;
    }

    // Off-edge: bag falls to the ground.
    const tol = edgeTolerance();
    const halfW = dims.width / 2 + tol;
    const halfL = dims.length / 2 + tol;
    if (Math.abs(slideX) > halfW || Math.abs(slideZ) > halfL) {
      v.x = cosR * slideVx + sinR * slideVz;
      v.z = -sinR * slideVx + cosR * slideVz;
      v.y = -0.4;
      phase = "fly";
      onSlab = false;
      stopBagSlide();
      return;
    }

    // Slide settled on the slab. Bag becomes "rest" but stays on the
    // slab (onSlab=true) so a later bag can kick it back to "slide".
    if (
      Math.abs(slideVx) < tune.bagSettleThreshold &&
      Math.abs(slideVz) < tune.bagSettleThreshold
    ) {
      stopBagSlide();
      slideVx = 0;
      slideVz = 0;
      // Lip capture — the bag has come to rest. If its center is
      // hanging just PAST the hole rim (within holeLipCapture metres),
      // tip it in rather than letting it perch on the edge. Bags barely
      // slide after landing, so a throw aimed at the hole that lands a
      // couple cm short would otherwise score 1 instead of the 3 it was
      // headed for. We snap the center the last bit inside the rim so
      // the geometric outcomeAt() reads in_hole, then drop it through —
      // looks like the bag tipping off the lip into the hole.
      const lip = tune.holeLipCapture;
      if (lip > 0) {
        // Vector from the hole CENTER to the bag, in slab-local space.
        const ox = slideX; // hole center local x = 0
        const oz = slideZ - holeZ;
        const d = Math.hypot(ox, oz);
        if (d > dims.holeRadius && d < dims.holeRadius + lip) {
          // Re-seat the center at 60% of the rim radius from the hole
          // center, along the direction it was hanging, so it reads
          // cleanly as in_hole and drops straight down the throat.
          const k = (dims.holeRadius * 0.6) / d;
          slideX = ox * k;
          slideZ = holeZ + oz * k;
          mesh.position.x = center.x + cosR * slideX + sinR * slideZ;
          mesh.position.z = center.z + (-sinR * slideX + cosR * slideZ);
          mesh.position.y =
            restedBoard.topYAtLocal(slideZ) + getTuning().bagThickness / 2;
          beginDrop();
          return;
        }
      }
      settle();
    }
  }

  function settle() {
    if (phase === "rest") return;
    phase = "rest";
    stopBagSlide();
    // Snap to the CANONICAL resting pose — the same orientation a
    // remote device computes when it replays this throw as a scripted
    // arc from the stored landing. Without this, the originator (who
    // ran real physics) and the remote (who replays) show the bag at
    // different yaw/roll. onSlab bags take the rake pitch; ground bags
    // lie flat. Yaw is hashed from the resting position so it's
    // identical everywhere.
    const t = getTuning();
    if (onSlab && restedBoard) {
      const pitchOffsetRad = (t.bagLandedPitchOffsetDeg * Math.PI) / 180;
      const yRot = restedBoard.group.rotation.y;
      mesh.rotation.x = -dims.rake * Math.cos(yRot) + pitchOffsetRad;
    } else {
      mesh.rotation.x = 0;
    }
    mesh.rotation.z = 0;
    mesh.rotation.y = seededYaw(mesh.position.x, mesh.position.z);
    resolveSim?.({
      outcome: "miss", // outer simulate() recomputes from board
      resting: {
        x: mesh.position.x,
        y: mesh.position.y,
        z: mesh.position.z,
      },
    });
  }

  // Used when the bag hits the ground (no slab contact retained).
  function finalize() {
    if (phase === "rest") return;
    onSlab = false;
    settle();
  }

  function getSlabLocal(): { x: number; z: number } | null {
    if (!onSlab) return null;
    return { x: slideX, z: slideZ };
  }

  function kickSlide(dvx: number, dvz: number): void {
    if (!onSlab) return;
    slideVx += dvx;
    slideVz += dvz;
    if (phase === "rest") phase = "slide";
    if (Math.hypot(slideVx, slideVz) > 0.4) startBagSlide();
    // Brief squash when another bag whacks us. Intensity is bounded
    // (collision impulses are smaller than landing thumps), so this
    // reads as a soft cushion not a slap.
    const impulse = Math.hypot(dvx, dvz);
    const intensity = Math.min(0.5, impulse * 0.8);
    if (intensity > 0.05) applySquashImpact(intensity);
  }

  function simulate(board: BoardHandle): Promise<ThrowResult> {
    restedBoard = board;
    return new Promise((resolve) => {
      resolveSim = (r) => {
        // Scripted bags carry their forced outcome through r.resting →
        // skip the geometric outcomeAt resolution and emit the
        // planned outcome directly. Physics drift cannot lie about
        // the ghost's score here.
        if (scripted) {
          resolve({ outcome: scripted.outcome, resting: r.resting });
          return;
        }
        const outcome = board.outcomeAt(r.resting);
        resolve({ outcome, resting: r.resting });
      };
    });
  }

  return { mesh, step, simulate, getSlabLocal, kickSlide };
}

function boardLocal(
  board: BoardHandle,
  worldPos: Vector3,
): { x: number; z: number } {
  const center = board.group.getWorldPosition(new Vector3());
  const rot = board.group.rotation.y;
  const dx = worldPos.x - center.x;
  const dz = worldPos.z - center.z;
  const cos = Math.cos(-rot);
  const sin = Math.sin(-rot);
  return { x: cos * dx + sin * dz, z: -sin * dx + cos * dz };
}

// Rebuilds a stacked pile of N bags at the given group's local origin.
// The group's world transform (pile.position) places the stack in the
// scene — see the createVirtualGame setup for team1/team2 anchors.
// Each bag wiggles a few cm in x/z so the pile reads as "thrown
// down" rather than mechanically stacked.
function rebuildPile(
  pile: Group,
  count: number,
  bagAsset: BagAsset,
): void {
  while (pile.children.length) {
    const c = pile.children[0]!;
    pile.remove(c);
    disposeObject(c);
  }
  // Pre-build the material(s) — same for every bag in the pile.
  // Imaged bags wrap one material across all faces (top-down UV
  // projection makes sides + bevels sample the image's edge pixels,
  // which encode the bag body color). Color-only bags get the
  // two-material split.
  function buildPileBagMaterial():
    | MeshStandardMaterial
    | [MeshStandardMaterial, MeshStandardMaterial] {
    if (bagAsset.topImage) {
      const mat = new MeshStandardMaterial({
        color: 0xffffff,
        roughness: bagAsset.roughness,
      });
      applyFabricFinish(mat);
      loadImageTexture(bagAsset.topImage).then((tex) => {
        if (!tex) return;
        applyBagTextureTransform(tex, bagAsset);
        mat.map = tex;
        mat.needsUpdate = true;
      });
      return mat;
    }
    const topMat = new MeshStandardMaterial({
      color: bagAsset.color,
      roughness: bagAsset.roughness,
    });
    const sideMat = new MeshStandardMaterial({
      color: bagAsset.color,
      roughness: Math.min(1, bagAsset.roughness + 0.03),
    });
    applyFabricFinish(topMat);
    applyFabricFinish(sideMat);
    return [topMat, sideMat];
  }
  for (let i = 0; i < count; i++) {
    const bag = new Mesh(
      makeBagGeometry(getTuning().bagSize, getTuning().bagThickness),
      buildPileBagMaterial(),
    );
    bag.position.set(
      (Math.random() - 0.5) * 0.06,
      getTuning().bagThickness / 2 + i * (getTuning().bagThickness * 0.85),
      (Math.random() - 0.5) * 0.08,
    );
    bag.rotation.y = (Math.random() - 0.5) * 0.5;
    // Match the finger bag's scale so piled "remaining bags" read
    // as the same physical object as the one in the player's hand
    // and as the in-flight + settled bags. fingerBagScale is the
    // single source of bag-size truth across every visible bag.
    bag.scale.setScalar(getTuning().fingerBagScale);
    bag.castShadow = true;
    bag.receiveShadow = true;
    pile.add(bag);
  }
}


export function disposeObject(obj: Object3D) {
  obj.traverse((c) => {
    const m = c as Mesh & { material?: Material | Material[] };
    if (m.geometry) m.geometry.dispose();
    if (m.material) {
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      mats.forEach((mat) => mat.dispose());
    }
  });
}

// Built-in registry re-export for callers that want a quick reference
// to the scene catalog without importing the assets module directly.
export { BUILT_IN_REGISTRY } from "./virtual3d-assets";
export type { GameLoadout, AssetRegistry } from "./virtual3d-assets";
